import { useState, useEffect } from "react";

const store = {
  get: async (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set: async (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
  list: async (prefix) => { try { return Object.keys(localStorage).filter(k => k.startsWith(prefix)); } catch { return []; } },
};
const genId = () => Math.random().toString(36).substr(2,9).toUpperCase();

// ── Carrier SOB Links ─────────────────────────────────────────────────────────
const CARRIERS = ["Aetna","AmeriHealth","Anthem / Elevance","Cigna / Health Spring","Devoted Health","Humana","Molina","Mutual of Omaha","UHC / UnitedHealthcare","Wellcare"];
const PLAN_BENEFIT_FIELDS = ["planType","premium","partBGiveback","moop","deductible","inpatientCopay","pcpCopay","specialistCopay","erCopay","mentalHealthCopay","rxDeductible","pharmacyNetwork","tier1Copay","tier2Copay","tier3Copay","tier4Copay","tier5Copay"];

const SOB_LINKS = {
  "humana": "https://www.humana.com/medicare/medicare-resources/plan-documents",
  "aetna": "https://www.aetnamedicare.com/en/find-a-plan/plan-documents.html",
  "uhc": "https://www.uhcmedicaresolutions.com/plan-documents",
  "united": "https://www.uhcmedicaresolutions.com/plan-documents",
  "unitedhealthcare": "https://www.uhcmedicaresolutions.com/plan-documents",
  "cigna": "https://www.cigna.com/medicare/plan-documents",
  "anthem": "https://www.anthem.com/medicare/plan-documents",
  "devoted": "https://www.devoted.com/member-resources",
  "amerihealth": "https://www.amerihealth.com/medicare/plan-documents",
  "wellcare": "https://www.wellcare.com/Medicare/Members/Plan-Documents",
  "centene": "https://www.centene.com/medicare",
  "molina": "https://www.molinahealthcare.com/members/medicare",
};


const getSOBLink = (carrier) => {
  if (!carrier) return "https://www.medicare.gov/plan-compare";
  const key = carrier.toLowerCase();
  for (const [name, url] of Object.entries(SOB_LINKS)) {
    if (key.includes(name)) return url;
  }
  return "https://www.medicare.gov/plan-compare";
};


const APPROVAL_REASONS = [
  "No SEP Found",
  "CSNP Verification Needed",
  "DSNP Medicaid Level Question",
  "Plan Exception or Override",
  "Other Compliance Issue",
];

const sendSlackAlert = async (approvalRequest) => {
  try {
    await fetch("/api/slack-alert", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(approvalRequest) });
  } catch(e) { console.log("Slack notification failed:", e); }
};

const C = {
  // CCM Brand Colors
  bg:"#111B3D", bg2:"#1B2A5E", bg3:"#243570",
  header:"#1B2A5E", header2:"#111B3D",
  teal:"#1DADA8", teal2:"#17908C", tealBg:"rgba(29,173,168,0.1)", tealBorder:"rgba(29,173,168,0.35)",
  navy:"#1B2A5E", navy2:"#243570",
  blue:"#1B2A5E", text:"#1B2A5E", mid:"#4A5A7A", dim:"#8A9ABB",
  green:"#22C55E", greenBg:"rgba(34,197,94,0.1)", greenBorder:"rgba(34,197,94,0.35)",
  red:"#E8336E", redBg:"rgba(232,51,110,0.08)", redBorder:"rgba(232,51,110,0.3)",
  accent:"#1DADA8", warn:"#F59E0B",
  gold:"#1DADA8", gold2:"#17908C", goldBg:"rgba(29,173,168,0.1)", goldBorder:"rgba(29,173,168,0.35)",
  cardBg:"#1B2A5E", border:"rgba(255,255,255,0.08)",
};

const emptyMed = () => ({ name:"", dosage:"", frequency:"", tier:"", copay:"" });

// ── Responsive hook ───────────────────────────────────────────────────────────
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);
  useEffect(() => {
    const handle = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isDesktop;
};

const CCMLogo = ({ white=false, height=44, style={} }) => {
  const textColor = white ? "#ffffff" : "#1a2e4a";
  const subColor = white ? "rgba(255,255,255,0.85)" : "#3a7d9c";
  const accentColor = "#3ecfcf";
  const scale = height / 44;
  const w = Math.round(220 * scale);
  const h = Math.round(44 * scale);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={style}>
      <rect x={0} y={0} width={40 * scale} height={44 * scale} rx={6 * scale} fill={accentColor} />
      <text x={20 * scale} y={30 * scale} fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize={18 * scale} fontWeight="bold" fill="#ffffff" textAnchor="middle">CCM</text>
      <text x={48 * scale} y={18 * scale} fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize={14 * scale} fontWeight="bold" fill={textColor} letterSpacing={0.3}>Complete Care</text>
      <text x={49 * scale} y={33 * scale} fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize={8.5 * scale} fontWeight="500" fill={subColor} letterSpacing={2}>MARKETING, INC.</text>
    </svg>
  );
};
const NEED_LEVELS = ["Not Satisfied","Somewhat Satisfied","Satisfied"];
const NEEDS_LIST = ["Dental","Vision","Scripts","Transportation"];

const BEN_AGREEMENTS = [
  { id:"ownDecisions", label:"I confirm that I make my own healthcare decisions." },
  { id:"sep", label:"I confirm my Special Enrollment Period (SEP) qualification as listed above. I understand this is the reason I am eligible to enroll outside of the Open Enrollment Period." },
  { id:"pcp", label:"I confirm my Primary Care Physician as listed above. I understand that if my PCP is out of network I may need to choose a new provider or pay out of pocket." },
  { id:"meds", label:"I confirm the medications listed above are accurate to the best of my knowledge. I understand I should verify formulary coverage with my pharmacist." },
  { id:"ssbci", label:"I understand that Special Supplemental Benefits are not guaranteed and are only available once eligibility is confirmed by the health plan after enrollment." },
  { id:"replaces", label:"I understand this plan will replace my existing coverage on the effective date listed above." },
  { id:"plan", label:"I confirm I wish to enroll in the Medicare Advantage plan presented to me today. I understand what I am enrolling in and I am doing so of my own free will." },
];

// ── UI Primitives ─────────────────────────────────────────────────────────────
const iStyle = { width:"100%", boxSizing:"border-box", background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:7, color:"#1a2e4a", padding:"10px 14px", fontSize:14, outline:"none", fontFamily:"'Helvetica Neue',Arial,sans-serif", boxShadow:"0 1px 3px rgba(0,0,0,0.08)" };
const F = ({ lbl, value, onChange, placeholder, type="text", half, ta }) => (
  <div style={{ flex:half?"1 1 45%":"1 1 100%", marginBottom:10 }}>
    {lbl && <div style={{ fontSize:11, color:"#1a2e4a", letterSpacing:1, textTransform:"uppercase", marginBottom:4, fontWeight:"bold" }}>{lbl}</div>}
    {ta ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||""} style={{ ...iStyle, minHeight:70, resize:"vertical" }} />
        : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||""} style={iStyle} />}
  </div>
);
const Sel = ({ lbl, value, onChange, options }) => (
  <div style={{ marginBottom:10 }}>
    {lbl && <div style={{ fontSize:11, color:"#1a2e4a", letterSpacing:1, textTransform:"uppercase", marginBottom:4, fontWeight:"bold" }}>{lbl}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{ ...iStyle, background:"#ffffff", color:"#1a2e4a" }}>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const Chk = ({ checked, onClick, children }) => (
  <div onClick={onClick} style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", padding:"9px 0", borderBottom:`1px solid rgba(26,58,110,0.4)` }}>
    <div style={{ width:22, height:22, borderRadius:5, border:checked?`2px solid ${C.teal}`:`2px solid ${C.border}`, background:checked?C.teal:"#ffffff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:12, color:C.bg, fontWeight:"bold", marginTop:1 }}>{checked?"✓":""}</div>
    <span style={{ fontSize:13, color:"#1a2e4a", lineHeight:1.5, fontWeight:"500" }}>{children}</span>
  </div>
);
const Script = ({ children, alert, red, mustread }) => (
  <div style={{ background:red||mustread?C.redBg:alert?C.redBg:C.tealBg, border:`1px solid ${red||mustread||alert?C.redBorder:C.tealBorder}`, borderRadius:8, padding:"12px 14px", marginBottom:10, fontSize:14, color:red||mustread?"#c00000":"#1a2e4a", lineHeight:1.8, fontWeight:red||mustread?"bold":"500" }}>
    {children}
  </div>
);

const MustRead = ({ children }) => (
  <div style={{ background:"rgba(192,0,0,0.07)", border:"2px solid #c00000", borderRadius:8, padding:"12px 14px", marginBottom:10, fontSize:13, color:"#c00000", lineHeight:1.8, fontWeight:"bold" }}>
    🔴 MUST READ: {children}
  </div>
);

const RedNote = ({ children }) => (
  <div style={{ background:"rgba(192,0,0,0.06)", border:`1px solid #c00000`, borderRadius:7, padding:"10px 12px", marginBottom:10, fontSize:13, color:"#c00000", lineHeight:1.6, fontWeight:"bold" }}>
    ⚠️ {children}
  </div>
);
const Note = ({ children }) => (
  <div style={{ background:"rgba(58,125,156,0.1)", border:`1px solid rgba(58,125,156,0.4)`, borderRadius:7, padding:"10px 12px", marginBottom:10, fontSize:13, color:"#2a5a7a", lineHeight:1.6, fontWeight:"500" }}>
    ▶ {children}
  </div>
);
const Warn = ({ children }) => (
  <div style={{ background:"rgba(234,106,106,0.1)", border:`1px solid ${C.redBorder}`, borderRadius:7, padding:"9px 12px", marginBottom:10, fontSize:12, color:C.red, lineHeight:1.6 }}>
    ⚠ {children}
  </div>
);
const SecHead = ({ num, icon, title, done }) => (
  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
    <span style={{ fontSize:17 }}>{icon}</span>
    <span style={{ fontSize:12, color:done?"#1a7a4a":"#1a2e4a", letterSpacing:1.5, textTransform:"uppercase", fontWeight:"bold", flex:1 }}>{num}. {title}</span>
    {done && <span style={{ fontSize:11, color:C.green, background:C.greenBg, border:`1px solid ${C.greenBorder}`, borderRadius:20, padding:"2px 10px" }}>✓ Done</span>}
  </div>
);
const Card = ({ children, highlight, warn }) => (
  <div style={{ background: highlight?C.tealBg: warn?C.redBg:"#ffffff", border:`1px solid ${highlight?C.tealBorder:warn?C.redBorder:C.border}`, borderRadius:12, padding:18, marginBottom:16, boxShadow:"0 2px 8px rgba(58,125,156,0.06)" }}>
    {children}
  </div>
);
const Btn = ({ onClick, children, disabled, secondary }) => (
  <button onClick={onClick} disabled={disabled} style={{ width:"100%", padding:"13px 20px", background:disabled?"#e0e8f0":secondary?"transparent":`linear-gradient(90deg,${C.header},${C.teal})`, border:secondary?`1px solid ${C.gold}`:"none", borderRadius:9, color:disabled?"#aac0d0":secondary?C.teal:"#ffffff", fontSize:14, fontWeight:"bold", cursor:disabled?"not-allowed":"pointer", letterSpacing:0.5, marginBottom:10, fontFamily:"Georgia,serif" }}>
    {children}
  </button>
);
const ImgUpload = ({ label, value, onChange }) => (
  <div style={{ marginBottom:12 }}>
    <div style={{ fontSize:10, color:C.mid, letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
    <input type="file" accept="image/*" multiple onChange={e=>{
      const files=Array.from(e.target.files);
      Promise.all(files.map(f=>new Promise(res=>{ const r=new FileReader(); r.onload=ev=>res(ev.target.result); r.readAsDataURL(f); }))).then(imgs=>onChange([...(value||[]),...imgs]));
    }} style={{ fontSize:12, color:C.mid, marginBottom:8, display:"block" }} />
    {value&&value.length>0 && <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>{value.map((img,i)=><img key={i} src={img} alt="" style={{ width:80, height:60, objectFit:"cover", borderRadius:5, border:`1px solid ${C.blue}` }} />)}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════
function EnrollHerePortal({ onBack, isDesktop }) {
  const stored = localStorage.getItem("ccm_enrollment") || localStorage.getItem("clearview_enrollment");
  const enrollment = stored ? JSON.parse(stored) : null;
  const bene = enrollment?.bene || {};
  const fullName = `${bene.firstName||""} ${bene.lastName||""}`.trim();
  const [status, setStatus] = React.useState("");

  const triggerExtension = () => {
    if (!enrollment) { setStatus("no_data"); return; }
    window.dispatchEvent(new CustomEvent("ccm_autofill", { detail: enrollment }));
    setStatus("sent");
    setTimeout(() => setStatus(""), 4000);
  };

  return (
    <div style={{ fontFamily:"'Helvetica Neue',Arial,sans-serif", background:C.bg, minHeight:"100vh", color:C.text }}>
      {/* Header */}
      <div style={{ background:"#ffffff", borderBottom:"3px solid #c0134e", padding:"12px 24px", position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 8px rgba(0,0,0,0.08)", display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onBack} style={{ background:"rgba(58,125,156,0.1)", border:`1px solid ${C.border}`, color:C.header, fontSize:14, cursor:"pointer", padding:"5px 12px", borderRadius:6, fontWeight:"bold" }}>‹ Back</button>
        <CCMLogo height={isDesktop ? 38 : 28} />
        <div style={{ fontSize: isDesktop ? 16 : 13, color:"#c0134e", fontWeight:"bold" }}>🎙️ Enroll Here</div>
      </div>

      <div style={{ padding: isDesktop ? "32px 40px" : "20px 16px", maxWidth:860, margin:"0 auto" }}>

        {/* Open Enroll Here button */}
        <div style={{ background:"#ffffff", border:"2px solid #c0134e", borderRadius:14, padding:24, marginBottom:20, boxShadow:"0 4px 16px rgba(192,19,78,0.12)", textAlign:"center" }}>
          <div style={{ fontSize:36, marginBottom:8 }}>🎙️</div>
          <div style={{ fontSize:18, fontWeight:"bold", color:"#1a2e4a", marginBottom:6 }}>Enroll Here Dialer</div>
          <div style={{ fontSize:13, color:C.mid, marginBottom:16 }}>Log in to start your recorded line. Come back here to autofill the CRM.</div>
          <button onClick={()=>window.open("https://dialer.enrollhere.com/agent","_blank")}
            style={{ background:"#c0134e", border:"none", borderRadius:10, color:"#ffffff", fontSize:15, fontWeight:"bold", padding:"14px 32px", cursor:"pointer", boxShadow:"0 4px 12px rgba(192,19,78,0.3)", width:"100%", maxWidth:360 }}>
            Open Enroll Here →
          </button>
        </div>

        {/* Autofill CRM button */}
        <div style={{ background:"#ffffff", border:`2px solid ${C.teal}`, borderRadius:14, padding:24, marginBottom:20, boxShadow:"0 4px 16px rgba(62,207,207,0.1)" }}>
          <div style={{ fontSize:14, fontWeight:"bold", color:C.header, marginBottom:4 }}>⚡ Autofill Enroll Here CRM</div>
          <div style={{ fontSize:12, color:C.mid, marginBottom:14, lineHeight:1.6 }}>
            Once you have a lead open in the Enroll Here dialer, tap the button below to autofill their information automatically from your enrollment data.
          </div>

          {enrollment ? (
            <div style={{ background:C.tealBg, border:`1px solid ${C.tealBorder}`, borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
              <div style={{ fontSize:12, color:C.teal, fontWeight:"bold", marginBottom:2 }}>✓ Enrollment data ready</div>
              <div style={{ fontSize:12, color:C.mid }}>{fullName || "Beneficiary"} — {enrollment.plan?.planName || "Plan not set"}</div>
            </div>
          ) : (
            <div style={{ background:"rgba(217,68,68,0.06)", border:"1px solid rgba(217,68,68,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
              <div style={{ fontSize:12, color:"#c00000", fontWeight:"bold", marginBottom:2 }}>⚠️ No enrollment data found</div>
              <div style={{ fontSize:12, color:C.mid }}>Complete an enrollment in the Agent Call Portal first, then come back here to autofill.</div>
            </div>
          )}

          <button onClick={triggerExtension} disabled={!enrollment}
            style={{ background: enrollment ? `linear-gradient(90deg,${C.header},${C.teal})` : "#e0e8f0", border:"none", borderRadius:10, color: enrollment ? "#ffffff" : "#8ab0c8", fontSize:14, fontWeight:"bold", padding:"13px 24px", cursor: enrollment ? "pointer" : "not-allowed", width:"100%", boxShadow: enrollment ? "0 4px 12px rgba(58,125,156,0.25)" : "none" }}>
            {status === "sent" ? "✓ Autofill Triggered — Check Enroll Here Tab" : status === "no_data" ? "⚠️ No Data — Complete Enrollment First" : "⚡ Autofill Enroll Here CRM"}
          </button>

          <div style={{ fontSize:11, color:C.dim, marginTop:8, textAlign:"center", lineHeight:1.5 }}>
            Make sure the CCM Chrome extension is installed and the Enroll Here tab is open
          </div>
        </div>

        {/* Fields that will be filled */}
        {enrollment && (
          <div style={{ background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:12, padding:18 }}>
            <div style={{ fontSize:11, color:C.header, textTransform:"uppercase", letterSpacing:1.5, fontWeight:"bold", marginBottom:12 }}>Fields That Will Be Filled</div>
            {[
              ["First Name", bene.firstName],
              ["Last Name", bene.lastName],
              ["Date of Birth", bene.dob],
              ["Medicare Number", bene.mbi],
              ["Phone", bene.phone],
              ["Email", bene.email],
              ["Address", bene.street],
              ["Gender", bene.gender],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}`, fontSize:12 }}>
                <span style={{ color:C.mid }}>{k}</span>
                <span style={{ color: v ? C.green : C.dim, fontWeight: v ? "bold" : "normal" }}>{v || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Root() {
  const [view, setView] = useState("home");
  const [enrollId, setEnrollId] = useState(null);
  const isDesktop = useIsDesktop();

  const navigate = (v) => {
    window.history.pushState({ view: v }, "", v === "home" ? "/" : `/${v}`);
    setView(v);
  };

  useEffect(() => {
    const h = window.location.hash;
    if (h.startsWith("#enroll-")) { setEnrollId(h.replace("#enroll-", "")); setView("beneficiary"); return; }
    const seg = window.location.pathname.replace(/^\//, "") || "home";
    if (["agent","beneficiary","manager"].includes(seg)) setView(seg);
    const onPop = (e) => { const v = (e.state?.view) || "home"; setView(v); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const goHome = () => { window.history.back(); };
  if(view==="agent") return <AgentApp onBack={goHome} isDesktop={isDesktop} />;
  if(view==="enrollhere") return <EnrollHerePortal onBack={goHome} isDesktop={isDesktop} />;
  if(view==="beneficiary") return <BeneficiaryApp enrollId={enrollId} onBack={goHome} isDesktop={isDesktop} />;
  if(view==="manager") return <ManagerApp onBack={goHome} isDesktop={isDesktop} />;
  return (
    <div style={{ fontFamily:"'Helvetica Neue',Arial,sans-serif", background:C.bg, minHeight:"100vh", color:C.text, minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start" }}>
      {/* CCM Header */}
      <div style={{ width:"100%", background:"#ffffff", borderBottom:"3px solid #3ecfcf", padding: isDesktop ? "32px 60px 22px" : "18px 20px 14px", textAlign:"center" }}>
        <div style={{ fontSize: isDesktop ? 28 : 22, fontWeight:"bold", color:"#0a1a2e", letterSpacing:0.5 }}>Complete Care Marketing, Inc.</div>
        <div style={{ fontSize:11, color:"#3a7d9c", marginTop:6, letterSpacing:0.5 }}>Medicare Advantage Enrollment Platform</div>
      </div>
      <div style={{ width:"100%", background:C.header, padding:"12px 20px", textAlign:"center" }}>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)", letterSpacing:0.5, marginBottom:4 }}>2026 Medicare Advantage Enrollment System</div>
        <div style={{ fontSize:16, color:"#3ecfcf", fontWeight:"bold", letterSpacing:1 }}>One app. One screen. One flow. Start to finish.</div>
      </div>
      <div style={{ width:"100%", background:C.header, padding: isDesktop ? "36px 60px 52px" : "24px 20px 36px", boxSizing:"border-box" }}>
        <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", letterSpacing:2.5, textTransform:"uppercase", textAlign:"center", marginBottom:18 }}>Select Your Portal</div>
        <div style={{ display:"grid", gridTemplateColumns: isDesktop ? "repeat(2, 1fr)" : "1fr", gap: isDesktop ? 20 : 12, maxWidth:960, margin:"0 auto", width:"100%" }}>
        {[
          { icon:"📞", title:"Agent Call Portal", sub:"Full guided script + enrollment", view:"agent" },
          { icon:"📱", title:"Beneficiary Portal", sub:"Review, agree & sign", view:"beneficiary" },
          { icon:"📊", title:"Manager Review", sub:"Review completed enrollments", view:"manager" },
          { icon:"🎙️", title:"Log In to Enroll Here", sub:"Recorded line — tap to open", view:"enrollhere" },
        ].map(b=>(
          <div key={b.view} onClick={()=>{
            if(b.view==="enrollhere") { window.open("https://app.enrollhere.com","_blank"); return; }
            if(b.view==="beneficiary") setEnrollId(null);
            navigate(b.view);
          }}
            style={{ background:"#ffffff", border:"none", borderRadius:14, padding: isDesktop ? "28px 24px" : "18px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:16, boxShadow:"0 6px 24px rgba(0,0,0,0.15)", borderLeft: b.view==="enrollhere" ? "8px solid #c0134e" : `8px solid ${C.teal}`, boxSizing:"border-box" }}>
            <div style={{ width:54, height:54, borderRadius:12, background: b.view==="enrollhere" ? "#c0134e" : C.header, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0 }}>{b.icon}</div>
            <div>
              <div style={{ fontSize:18, fontWeight:"bold", color:"#0a1a2e", letterSpacing:0.3 }}>{b.title}</div>
              <div style={{ fontSize:13, color:"#1a4a6a", marginTop:5, fontWeight:"600" }}>{b.sub}</div>
            </div>
            <div style={{ marginLeft:"auto", color: b.view==="enrollhere" ? "#c0134e" : C.teal, fontSize:28, fontWeight:"bold" }}>›</div>
          </div>
        ))}
        </div>
      </div>
      {/* Daily login quick access */}
      <div style={{ width:"100%", background:"#ffffff", borderTop:`1px solid ${C.border}`, padding:"14px 20px" }}>
        <div style={{ fontSize:9, color:C.mid, textTransform:"uppercase", letterSpacing:1.5, fontWeight:"bold", marginBottom:10, textAlign:"center" }}>Daily Login</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, maxWidth:900, margin:"0 auto" }}>
          {[
            { label:"🎙️ Enroll Here", url:"https://app.enrollhere.com/login", color:"#c0134e" },
            { label:"☀️ Sunfire", url:"https://www.sunfirematrix.com", color:"#f59e0b" },
            { label:null, img:"/cms-logo.svg", url:"https://portal.cms.gov/portal", color:C.header },
            { label:"🔍 Jarvis", url:"https://www.uhcjarvis.com/content/jarvis/en/sign_in.html", color:"#0072CE" },
          ].map(p=>(
            <div key={p.url} onClick={()=>window.open(p.url,"_blank")}
              style={{ background:C.bg, border:`1px solid ${p.color}`, borderRadius:8, padding:"8px 6px", cursor:"pointer", textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center" }}>
              {p.img
                ? <img src={p.img} alt="CMS" style={{ height:18, filter:"brightness(0) invert(1)" }} />
                : <div style={{ fontSize:11, fontWeight:"bold", color:"#ffffff" }}>{p.label}</div>}
            </div>
          ))}
        </div>
        <div style={{ fontSize:10, color:C.dim, letterSpacing:0.5, textAlign:"center", marginTop:12 }}>2026 Enrollment Intelligence System — Built by Complete Care Marketing, Inc.</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT APP — Exact script order
// ═══════════════════════════════════════════════════════════════════════════════
function MedTierLookup() {
  return null;
}

function StackCalculator({ plan, up }) {
  const parseVal = v => parseFloat((v||"0").replace(/[^0-9.-]/g,"")) || 0;

  // Benefit rows with default frequency and type
  // type "benefit" = higher is better (dental allowance, OTC card etc)
  // type "cost"    = lower is better (premium, copay, out of pocket)
  const BENEFITS = [
    { key:"dental",    label:"🦷 Dental Allowance",          defaultFreq:"annual",    type:"benefit" },
    { key:"vision",    label:"👓 Vision Allowance",          defaultFreq:"annual",    type:"benefit" },
    { key:"otc",       label:"🛒 OTC / Drugstore",           defaultFreq:"quarterly", type:"benefit" },
    { key:"food",      label:"🍎 Food Card",                 defaultFreq:"monthly",   type:"benefit" },
    { key:"transport", label:"🚗 Transportation",            defaultFreq:"annual",    type:"benefit" },
    { key:"giveback",  label:"💰 Part B Giveback",           defaultFreq:"monthly",   type:"benefit" },
    { key:"fitness",   label:"🏃 Fitness / Silver Sneakers", defaultFreq:"annual",    type:"benefit" },
    { key:"hearing",   label:"👂 Hearing",                   defaultFreq:"annual",    type:"benefit" },
    { key:"premium",   label:"💵 Plan Premium (Cost)",       defaultFreq:"monthly",   type:"cost" },
    { key:"rx",        label:"💊 Rx Out of Pocket (Cost)",   defaultFreq:"quarterly", type:"cost" },
    { key:"hospital",  label:"🏥 Hospital Copay (Cost)",     defaultFreq:"annual",    type:"cost" },
  ];

  const FREQ_MULTIPLIER = { monthly:12, quarterly:4, annual:1 };

  const toAnnual = (val, freq) => parseVal(val) * (FREQ_MULTIPLIER[freq] || 1);

  const totalCurr = BENEFITS.reduce((sum,b) => {
    const freq = plan[`stack_freq_${b.key}`] || b.defaultFreq;
    const val = toAnnual(plan[`stack_curr_${b.key}`], freq);
    return sum + val;
  }, 0);

  const totalNew = BENEFITS.reduce((sum,b) => {
    const freq = plan[`stack_freq_${b.key}`] || b.defaultFreq;
    const val = toAnnual(plan[`stack_new_${b.key}`], freq);
    return sum + val;
  }, 0);

  // NET savings — direction depends on type:
  // benefit: new > current = GAIN (more benefit)
  // cost:    new < current = GAIN (paying less)
  const netByBenefit = BENEFITS.map(b => {
    const freq = plan[`stack_freq_${b.key}`] || b.defaultFreq;
    const curr = toAnnual(plan[`stack_curr_${b.key}`], freq);
    const newVal = toAnnual(plan[`stack_new_${b.key}`], freq);
    // For costs: gain = current - new (paying less is good)
    // For benefits: gain = new - current (getting more is good)
    const diff = b.type === "cost" ? curr - newVal : newVal - curr;
    return { key:b.key, label:b.label, diff, curr, newVal, type:b.type };
  }).filter(b => b.curr > 0 || b.newVal > 0);

  const savings = netByBenefit.reduce((sum,b) => sum + b.diff, 0);
  const gains = netByBenefit.filter(b=>b.diff>0);
  const losses = netByBenefit.filter(b=>b.diff<0);

  const freqColor = { monthly:"#3a7d9c", quarterly:"#e8a030", annual:"#2a9a6a" };
  const freqLabel = { monthly:"mo", quarterly:"qtr", annual:"yr" };

  return (
    <div>
      {/* Benefit rows with frequency selector */}
      <div style={{ marginBottom:12 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1.6fr 0.8fr 0.8fr 0.9fr", gap:6, marginBottom:8 }}>
          <div style={{ fontSize:9, color:C.mid, textTransform:"uppercase", letterSpacing:1, fontWeight:"bold" }}>Benefit / Cost</div>
          <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", letterSpacing:1, fontWeight:"bold" }}>What They Pay / Get Now</div>
          <div style={{ fontSize:9, color:"#2a9a6a", textTransform:"uppercase", letterSpacing:1, fontWeight:"bold" }}>What They'll Pay / Get</div>
          <div style={{ fontSize:9, color:C.mid, textTransform:"uppercase", letterSpacing:1, fontWeight:"bold" }}>Frequency</div>
        </div>
        {BENEFITS.map(b=>{
          const freq = plan[`stack_freq_${b.key}`] || b.defaultFreq;
          const currVal = parseVal(plan[`stack_curr_${b.key}`]);
          const newVal = parseVal(plan[`stack_new_${b.key}`]);
          const currAnnual = toAnnual(plan[`stack_curr_${b.key}`], freq);
          const newAnnual = toAnnual(plan[`stack_new_${b.key}`], freq);
          const diff = newAnnual - currAnnual;
          return (
            <div key={b.key} style={{ marginBottom:6 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1.6fr 0.8fr 0.8fr 0.9fr", gap:6, alignItems:"center" }}>
                <div style={{ fontSize:12, color:"#1a2e4a", fontWeight:"600" }}>
                  {b.label}
                  <span style={{ fontSize:9, marginLeft:5, color: b.type==="cost"?"#c00000":"#2a9a6a", background: b.type==="cost"?"rgba(217,68,68,0.08)":"rgba(42,154,106,0.08)", borderRadius:3, padding:"1px 5px", fontWeight:"bold" }}>
                    {b.type==="cost"?"lower=better":"higher=better"}
                  </span>
                </div>
                <input
                  value={plan[`stack_curr_${b.key}`]||""}
                  onChange={e=>up(`stack_curr_${b.key}`,e.target.value)}
                  placeholder="$0"
                  style={{ background:"rgba(217,68,68,0.05)", border:`1px solid rgba(217,68,68,0.25)`, borderRadius:6, color:"#c00000", padding:"6px 7px", fontSize:13, fontWeight:"bold", width:"100%", boxSizing:"border-box", textAlign:"center", outline:"none" }}
                />
                <input
                  value={plan[`stack_new_${b.key}`]||""}
                  onChange={e=>up(`stack_new_${b.key}`,e.target.value)}
                  placeholder="$0"
                  style={{
                    background: b.type==="cost"
                      ? (parseFloat(plan[`stack_new_${b.key}`]||0) < parseFloat(plan[`stack_curr_${b.key}`]||0) ? "rgba(42,154,106,0.08)" : "rgba(217,68,68,0.05)")
                      : (parseFloat(plan[`stack_new_${b.key}`]||0) > parseFloat(plan[`stack_curr_${b.key}`]||0) ? "rgba(42,154,106,0.08)" : "rgba(217,68,68,0.05)"),
                    border: b.type==="cost"
                      ? (parseFloat(plan[`stack_new_${b.key}`]||0) < parseFloat(plan[`stack_curr_${b.key}`]||0) ? "1px solid rgba(42,154,106,0.4)" : "1px solid rgba(217,68,68,0.25)")
                      : (parseFloat(plan[`stack_new_${b.key}`]||0) > parseFloat(plan[`stack_curr_${b.key}`]||0) ? "1px solid rgba(42,154,106,0.4)" : "1px solid rgba(217,68,68,0.25)"),
                    borderRadius:6,
                    color: b.type==="cost"
                      ? (parseFloat(plan[`stack_new_${b.key}`]||0) < parseFloat(plan[`stack_curr_${b.key}`]||0) ? "#2a9a6a" : "#c00000")
                      : (parseFloat(plan[`stack_new_${b.key}`]||0) > parseFloat(plan[`stack_curr_${b.key}`]||0) ? "#2a9a6a" : "#c00000"),
                    padding:"6px 7px", fontSize:13, fontWeight:"bold", width:"100%", boxSizing:"border-box", textAlign:"center", outline:"none"
                  }}
                />
                <select
                  value={freq}
                  onChange={e=>up(`stack_freq_${b.key}`,e.target.value)}
                  style={{ background:"#fff", border:`1px solid ${freqColor[freq]}`, borderRadius:6, color:freqColor[freq], padding:"6px 5px", fontSize:11, fontWeight:"bold", width:"100%", boxSizing:"border-box" }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              {/* Show annual conversion if not already annual */}
              {(currVal > 0 || newVal > 0) && freq !== "annual" && (
                <div style={{ fontSize:10, color:C.mid, marginTop:2, marginLeft:2, display:"flex", gap:12 }}>
                  {currVal > 0 && <span style={{color:"#c00000"}}>= ${currAnnual.toFixed(0)}/yr</span>}
                  {newVal > 0 && <span style={{color:"#2a9a6a"}}>= ${newAnnual.toFixed(0)}/yr</span>}
                  {(currVal > 0 || newVal > 0) && diff !== 0 && <span style={{color: diff>0?"#2a9a6a":"#c00000", fontWeight:"bold"}}>{diff>0?"+":""}{diff.toFixed(0)}/yr</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div style={{ background: savings >= 0 ? "rgba(42,154,106,0.07)" : "rgba(217,68,68,0.05)", border:`2px solid ${savings >= 0 ? "#2a9a6a" : "#d94444"}`, borderRadius:10, padding:14 }}>
        <div style={{ fontSize:11, color:C.mid, textTransform:"uppercase", letterSpacing:1.5, fontWeight:"bold", marginBottom:10 }}>💰 Annual Money Stack Summary</div>
        <div style={{ fontSize:10, color:C.dim, marginBottom:10, fontStyle:"italic" }}>All values converted to annual — monthly ×12, quarterly ×4. Net difference per benefit.</div>

        {/* Gains breakdown */}
        {gains.length > 0 && (
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:10, color:"#2a9a6a", textTransform:"uppercase", letterSpacing:1, fontWeight:"bold", marginBottom:5 }}>✓ Where New Plan is Better</div>
            {gains.map(b=>(
              <div key={b.key} style={{ display:"flex", justifyContent:"space-between", padding:"5px 8px", background:"rgba(42,154,106,0.05)", borderRadius:6, marginBottom:3, border:"1px solid rgba(42,154,106,0.2)" }}>
                <span style={{ fontSize:12, color:"#1a2e4a" }}>{b.label}</span>
                <span style={{ fontSize:12, color:"#2a9a6a", fontWeight:"bold" }}>+${b.diff.toFixed(0)}/yr</span>
              </div>
            ))}
          </div>
        )}

        {/* Losses breakdown */}
        {losses.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:"#c00000", textTransform:"uppercase", letterSpacing:1, fontWeight:"bold", marginBottom:5 }}>✗ Where Current Plan is Better</div>
            {losses.map(b=>(
              <div key={b.key} style={{ display:"flex", justifyContent:"space-between", padding:"5px 8px", background:"rgba(217,68,68,0.05)", borderRadius:6, marginBottom:3, border:"1px solid rgba(217,68,68,0.2)" }}>
                <span style={{ fontSize:12, color:"#1a2e4a" }}>{b.label}</span>
                <span style={{ fontSize:12, color:"#c00000", fontWeight:"bold" }}>{b.diff.toFixed(0)}/yr</span>
              </div>
            ))}
          </div>
        )}

        {/* Net total */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
          <div style={{ textAlign:"center", background:"rgba(217,68,68,0.06)", borderRadius:8, padding:"10px 6px" }}>
            <div style={{ fontSize:10, color:"#c00000", textTransform:"uppercase", marginBottom:4, fontWeight:"bold" }}>Current</div>
            <div style={{ fontSize:16, color:"#c00000", fontWeight:"bold" }}>${totalCurr.toFixed(0)}<span style={{fontSize:9}}>/yr</span></div>
          </div>
          <div style={{ textAlign:"center", background:"rgba(42,154,106,0.07)", borderRadius:8, padding:"10px 6px" }}>
            <div style={{ fontSize:10, color:"#2a9a6a", textTransform:"uppercase", marginBottom:4, fontWeight:"bold" }}>New Plan</div>
            <div style={{ fontSize:16, color:"#2a9a6a", fontWeight:"bold" }}>${totalNew.toFixed(0)}<span style={{fontSize:9}}>/yr</span></div>
          </div>
          <div style={{ textAlign:"center", background: savings >= 0 ? "rgba(42,154,106,0.1)" : "rgba(217,68,68,0.08)", borderRadius:8, padding:"10px 6px", border:`2px solid ${savings >= 0 ? "#2a9a6a" : "#d94444"}` }}>
            <div style={{ fontSize:10, color: savings >= 0 ? "#2a9a6a" : "#d94444", textTransform:"uppercase", marginBottom:4, fontWeight:"bold" }}>Net Annual</div>
            <div style={{ fontSize:16, color: savings >= 0 ? "#2a9a6a" : "#d94444", fontWeight:"bold" }}>{savings >= 0 ? "+$" : "-$"}{Math.abs(savings).toFixed(0)}<span style={{fontSize:9}}>/yr</span></div>
          </div>
        </div>

        {savings > 0 && (
          <div style={{ textAlign:"center", background:`linear-gradient(90deg,${C.header},${C.teal})`, borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginBottom:4, letterSpacing:1, textTransform:"uppercase" }}>True Net Annual Value with New Plan</div>
            <div style={{ fontSize:30, color:"#ffffff", fontWeight:"bold" }}>${savings.toFixed(0)}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:4 }}>more in benefits & savings per year after all trade-offs</div>
          </div>
        )}
        {savings < 0 && (
          <div style={{ textAlign:"center", background:"rgba(217,68,68,0.08)", border:"2px solid #d94444", borderRadius:8, padding:"12px 16px", marginBottom:12 }}>
            <div style={{ fontSize:13, color:"#c00000", fontWeight:"bold" }}>⚠️ Net loss of ${Math.abs(savings).toFixed(0)}/yr — review trade-offs with client</div>
          </div>
        )}
        {/* Custom calculator */}
        <div style={{ marginTop:4 }}>
          <div style={{ fontSize:11, color:C.mid, textTransform:"uppercase", letterSpacing:1, fontWeight:"bold", marginBottom:6 }}>Quick Calculator</div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <input value={plan.calcA||""} onChange={e=>up("calcA",e.target.value)} placeholder="Amount 1" style={{ flex:"1 1 70px", background:"#fff", border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", fontSize:14, fontWeight:"bold", color:"#1a2e4a", outline:"none" }} />
            <select value={plan.calcOp||"+"} onChange={e=>up("calcOp",e.target.value)} style={{ background:"#fff", border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", fontSize:16, fontWeight:"bold", color:C.header }}>
              {["+","-","×","÷"].map(op=><option key={op} value={op}>{op}</option>)}
            </select>
            <input value={plan.calcB||""} onChange={e=>up("calcB",e.target.value)} placeholder="Amount 2" style={{ flex:"1 1 70px", background:"#fff", border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 10px", fontSize:14, fontWeight:"bold", color:"#1a2e4a", outline:"none" }} />
            <div style={{ fontSize:14, color:C.mid, fontWeight:"bold" }}>=</div>
            <div style={{ flex:"1 1 70px", background:C.tealBg, border:`1px solid ${C.tealBorder}`, borderRadius:6, padding:"8px 10px", fontSize:14, fontWeight:"bold", color:C.header, textAlign:"center" }}>
              {(()=>{
                const pv = v => parseFloat((v||"0").replace(/[^0-9.-]/g,""))||0;
                const a=pv(plan.calcA), b=pv(plan.calcB), op=plan.calcOp||"+";
                if(op==="+") return `$${(a+b).toFixed(2)}`;
                if(op==="-") return `$${(a-b).toFixed(2)}`;
                if(op==="×") return `$${(a*b).toFixed(2)}`;
                if(op==="÷") return b?`$${(a/b).toFixed(2)}`:"—";
                return "—";
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentApp({ onBack, isDesktop=false }) {
  const [agent, setAgent] = useState({ name:"", npn:"", company:"", phone:"", email:"" });
  const [agentSaved, setAgentSaved] = useState(false);
  const [bene, setBene] = useState({ firstName:"", lastName:"", dob:"", phone:"", email:"", street:"", street2:"", city:"", state:"", zip:"", mbi:"", medicaidNum:"", medicaidLevel:"None", hasMedicaid:"No", otherBenefits:"No", csnp:"No", csnpCondition:"", purposeOfCall:"", theWhy:"", benefitsFor:"Just Me", mailingDiff:false, mailStreet:"", mailCity:"", mailState:"", mailZip:"" });
  const [plan, setPlan] = useState({ planName:"", carrier:"", startDate:"", currentCarrier:"", pcpName:"", pcpId:"", pcpPhone:"", pcpFax:"", specialists:"", planType:"HMO", referrals:"Required", sepFound:"Yes", premium:"", partBGiveback:"", moop:"", deductible:"", inpatientCopay:"", pcpCopay:"", specialistCopay:"", erCopay:"", mentalHealthCopay:"", rxDeductible:"", pharmacyNetwork:"", tier1Copay:"", tier2Copay:"", tier3Copay:"", tier4Copay:"", tier5Copay:"" });
  const [planLibrary, setPlanLibrary] = useState([]);
  const [planEditMode, setPlanEditMode] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [meds, setMeds] = useState(Array.from({length:20},emptyMed));
  const [needs, setNeeds] = useState({});
  const [sec, setSec] = useState({});
  const [screenshots, setScreenshots] = useState({ marx:[], planComparison:[], enrollment:[] });
  const [generated, setGenerated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [approvalReason, setApprovalReason] = useState(APPROVAL_REASONS[0]);
  const [clientOnLine, setClientOnLine] = useState(true);
  const [approvalSent, setApprovalSent] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null); // null | 'pending' | 'approved' | 'declined'
  const [approvalNote, setApprovalNote] = useState("");
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);

  const ub=(k,v)=>setBene(p=>({...p,[k]:v}));
  const up=(k,v)=>setPlan(p=>({...p,[k]:v}));
  const ua=(k,v)=>setAgent(p=>({...p,[k]:v}));
  const done=(k)=>setSec(p=>({...p,[k]:true}));
  const fullName=`${bene.firstName} ${bene.lastName}`.trim();
  const progress=Object.values(sec).filter(Boolean).length;
  const totalSec=17;

  const fetchPlans = () => fetch("/api/plans").then(r=>r.json()).then(setPlanLibrary).catch(()=>{});
  useEffect(()=>{ store.get("agent_profile").then(d=>{ if(d){ setAgent(d); setAgentSaved(true); } }); fetchPlans(); },[]);
  const saveAgent=async()=>{ await store.set("agent_profile",agent); setAgentSaved(true); };

  const loadPlanFromLibrary = (planName) => {
    const found = planLibrary.find(p => p.carrier === plan.carrier && p.planName === planName);
    if (found) { setPlan(p => { const n={...p}; PLAN_BENEFIT_FIELDS.forEach(k=>{ n[k]=found[k]||""; }); n.planName=planName; return n; }); setPlanEditMode(false); }
    else { up("planName", planName); }
  };

  const savePlanToLibrary = async () => {
    if (!plan.carrier || !plan.planName) return;
    setPlanSaving(true);
    try {
      const payload = { carrier: plan.carrier, planName: plan.planName };
      PLAN_BENEFIT_FIELDS.forEach(k => { payload[k] = plan[k]||""; });
      await fetch("/api/plans", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload) });
      await fetchPlans();
      setPlanEditMode(false);
    } catch(e) { console.error(e); }
    setPlanSaving(false);
  };

  const requestApproval = async () => {
    const requestId = genId();
    const request = {
      id: requestId,
      enrollId: generated || "PRE-ENROLLMENT",
      agentName: agent.name || "Unknown Agent",
      agentNpn: agent.npn,
      clientName: fullName || "Unknown Client",
      clientPhone: bene.phone,
      reason: approvalReason,
      clientOnLine,
      status: "pending",
      timestamp: new Date().toISOString(),
      planName: plan.planName,
      carrier: plan.carrier,
      note: "",
    };
    await store.set(`approval:${requestId}`, request, true);
    await sendSlackAlert(request);
    setApprovalSent(true);
    setApprovalStatus("pending");
    // Poll for manager response every 5 seconds
    const poll = setInterval(async () => {
      const updated = await store.get(`approval:${requestId}`, true);
      if (updated?.status === "approved") {
        setApprovalStatus("approved");
        setApprovalNote(updated.managerNote || "");
        clearInterval(poll);
      } else if (updated?.status === "declined") {
        setApprovalStatus("declined");
        setApprovalNote(updated.managerNote || "");
        clearInterval(poll);
      }
    }, 5000);
  };

  const generateLink=async()=>{
    setSaving(true);
    const id=genId();
    await store.set(`enroll:${id}`,{ id, agent, bene, plan, meds:meds.filter(m=>m.name), needs, status:"pending", createdAt:new Date().toISOString() },true);
    setGenerated(id);
    setSaving(false);
  };

  const enrollLink=generated?`${window.location.origin}${window.location.pathname}#enroll-${generated}`:"";
  const smsText=`Hi ${bene.firstName||"[Name]"}, this is ${agent.name||"[Agent]"} from ${agent.company}. Here is your secure enrollment link for the ${plan.planName||"[Plan]"} plan starting ${plan.startDate||"[Date]"}. Please review and sign: ${enrollLink}`;
  const copy=(text,key)=>{ navigator.clipboard.writeText(text); setCopied(key); setTimeout(()=>setCopied(""),2200); };



  const wrap={ fontFamily:"'Helvetica Neue',Arial,sans-serif", background:C.bg, minHeight:"100vh", color:C.text, paddingBottom:100, display:"flex", flexDirection:"column" };

  const ApprovalPanel = () => (
    <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:520, zIndex:200, background:"#fff", borderTop:"3px solid #c00000", boxShadow:"0 -4px 20px rgba(0,0,0,0.15)", padding:16 }}>
      {!approvalSent ? <>
        <div style={{ fontSize:13, color:"#c00000", fontWeight:"bold", marginBottom:10 }}>🔴 Request Manager Approval</div>
        <div style={{ fontSize:10, color:C.mid, textTransform:"uppercase", marginBottom:4 }}>Reason</div>
        <select value={approvalReason} onChange={e=>setApprovalReason(e.target.value)} style={{ width:"100%", background:"#fff", border:"1px solid #c00000", borderRadius:7, color:"#c00000", padding:"8px 12px", fontSize:13, marginBottom:10, fontWeight:"bold" }}>
          {APPROVAL_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
        <div style={{ display:"flex", gap:8, marginBottom:12 }}>
          <div onClick={()=>setClientOnLine(true)} style={{ flex:1, textAlign:"center", padding:"10px 6px", borderRadius:7, border:`2px solid ${clientOnLine?"#c00000":C.border}`, background:clientOnLine?"rgba(192,0,0,0.08)":"#fff", cursor:"pointer", fontSize:12, color:clientOnLine?"#c00000":C.mid, fontWeight:"bold" }}>🔴 Client ON the Line</div>
          <div onClick={()=>setClientOnLine(false)} style={{ flex:1, textAlign:"center", padding:"10px 6px", borderRadius:7, border:`2px solid ${!clientOnLine?C.warn:C.border}`, background:!clientOnLine?"rgba(232,160,48,0.08)":"#fff", cursor:"pointer", fontSize:12, color:!clientOnLine?C.warn:C.mid, fontWeight:"bold" }}>🟡 No Client on Hold</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={requestApproval} style={{ flex:2, padding:"12px", background:"#c00000", border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:"bold", cursor:"pointer" }}>📤 Send to Manager + Slack</button>
          <button onClick={()=>setShowApprovalPanel(false)} style={{ flex:1, padding:"12px", background:"#f0f0f0", border:"none", borderRadius:8, color:C.mid, fontSize:13, cursor:"pointer" }}>Cancel</button>
        </div>
      </> : <>
        {approvalStatus==="pending" && <div style={{ textAlign:"center", padding:"10px 0" }}>
          <div style={{ fontSize:22, marginBottom:6 }}>⏳</div>
          <div style={{ fontSize:14, color:C.warn, fontWeight:"bold" }}>Waiting for Manager Approval</div>
          <div style={{ fontSize:12, color:C.mid, marginTop:4 }}>Slack notification sent • Checking every 5 seconds</div>
          <div style={{ fontSize:11, color:C.mid, marginTop:4 }}>Reason: {approvalReason} • {clientOnLine?"Client ON line":"No client on hold"}</div>
        </div>}
        {approvalStatus==="approved" && <div style={{ textAlign:"center", padding:"10px 0" }}>
          <div style={{ fontSize:22, marginBottom:6 }}>✅</div>
          <div style={{ fontSize:14, color:C.green, fontWeight:"bold" }}>Manager Approved — Proceed</div>
          {approvalNote && <div style={{ fontSize:12, color:C.mid, marginTop:4 }}>Note: {approvalNote}</div>}
          <button onClick={()=>{ setShowApprovalPanel(false); setApprovalSent(false); setApprovalStatus(null); }} style={{ marginTop:10, padding:"8px 20px", background:C.green, border:"none", borderRadius:7, color:"#fff", fontSize:13, fontWeight:"bold", cursor:"pointer" }}>Close</button>
        </div>}
        {approvalStatus==="declined" && <div style={{ textAlign:"center", padding:"10px 0" }}>
          <div style={{ fontSize:22, marginBottom:6 }}>❌</div>
          <div style={{ fontSize:14, color:C.red, fontWeight:"bold" }}>Manager Declined</div>
          {approvalNote && <div style={{ fontSize:12, color:C.mid, marginTop:4 }}>Reason: {approvalNote}</div>}
          <button onClick={()=>{ setShowApprovalPanel(false); setApprovalSent(false); setApprovalStatus(null); }} style={{ marginTop:10, padding:"8px 20px", background:C.red, border:"none", borderRadius:7, color:"#fff", fontSize:13, fontWeight:"bold", cursor:"pointer" }}>Close</button>
        </div>}
      </>}
    </div>
  );

  return (
    <div style={wrap}>
      {showApprovalPanel && <ApprovalPanel />}
      {approvalStatus==="approved" && !showApprovalPanel && (
        <div style={{ background:C.greenBg, border:`1px solid ${C.greenBorder}`, padding:"10px 16px", textAlign:"center", fontSize:13, color:C.green, fontWeight:"bold" }}>
          ✅ Manager Approved — You are clear to proceed {approvalNote && `— "${approvalNote}"`}
        </div>
      )}
      {/* Sticky Header */}
      <div style={{ background:"#ffffff", borderBottom:`3px solid ${C.teal}`, padding: isDesktop ? "14px 32px" : "10px 16px", position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 8px rgba(0,0,0,0.08)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={onBack} style={{ background:"rgba(58,125,156,0.1)", border:`1px solid ${C.border}`, color:C.header, fontSize:18, cursor:"pointer", padding:"4px 10px", borderRadius:6, fontWeight:"bold" }}>‹</button>
          <CCMLogo height={isDesktop ? 38 : 28} />
          <div style={{ flex:1 }}>
            <div style={{ fontSize: isDesktop ? 16 : 13, color:C.header, fontWeight:"bold" }}>📞 Agent Call Portal</div>
            <div style={{ fontSize:11, color:C.mid }}>{fullName||"New Enrollment"}</div>
          </div>
          <div style={{ textAlign:"right", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
            <div style={{ fontSize:11, color:progress>=totalSec?C.teal:"rgba(255,255,255,0.9)" }}>{progress}/{totalSec} steps</div>
            <div style={{ width:80, height:4, background:"rgba(255,255,255,0.3)", borderRadius:2, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${(progress/totalSec)*100}%`, background:`linear-gradient(90deg,${C.gold},${C.gold2})`, borderRadius:2, transition:"width 0.3s" }} />
            </div>
            <button onClick={()=>{ setShowApprovalPanel(true); setApprovalSent(false); setApprovalStatus(null); }} style={{ background:"#c00000", border:"none", borderRadius:5, color:"#fff", fontSize:10, padding:"4px 8px", cursor:"pointer", fontWeight:"bold", letterSpacing:0.5 }}>🔴 Need Approval</button>
          </div>
        </div>
      </div>

      <div style={{ padding: isDesktop ? "28px 32px" : "16px 14px", maxWidth: isDesktop ? 1100 : "100%", margin:"0 auto", width:"100%", boxSizing:"border-box" }}>

        {/* ── 1. AGENT SETUP ── */}
        <Card>
          <SecHead num={1} icon="👤" title="Agent Setup" done={agentSaved} />
          <div style={{ display:"flex", gap:10 }}>
            <F lbl="Your Full Name" value={agent.name} onChange={v=>ua("name",v)} half />
            <F lbl="Your NPN" value={agent.npn} onChange={v=>ua("npn",v)} half />
          </div>
          <F lbl="Company" value={agent.company} onChange={v=>ua("company",v)} />
          <F lbl="Your Direct Phone Number" value={agent.phone} onChange={v=>ua("phone",v)} type="tel" placeholder="Number beneficiary can reach you at" />
          <F lbl="Your Enroll Here Email" value={agent.email} onChange={v=>ua("email",v)} type="email" placeholder="Used to auto-copy on login" />
          <Btn onClick={saveAgent}>💾 Save Agent Info Permanently</Btn>
          {agentSaved && <div style={{ fontSize:12, color:C.green, marginTop:-6, marginBottom:6 }}>✓ Saved — pre-fills every call</div>}
        </Card>

        {/* ── CARRIER PORTALS ── */}
        <Card>
          <SecHead num="🔗" icon="" title="Carrier & Cross-Sell Portals" done={false} />
          <div style={{ fontSize:11, color:C.mid, marginBottom:12, letterSpacing:0.5 }}>One tap to open any portal — opens in new tab so you never lose your place.</div>

          {/* ── DAILY LOGIN PORTALS ── */}
          <div style={{ fontSize:10, color:C.header, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:"bold" }}>Daily Login</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
            {[
              { label:"🎙️ Enroll Here", sub:"Recorded line — log in first", url:"https://app.enrollhere.com/login", color:"#c0134e", copyEmail:true },
              { label:"☀️ Sunfire", sub:"Plan lookup & enrollment", url:"https://www.sunfirematrix.com", color:"#f59e0b" },
              { label:null, img:"/cms-logo.svg", sub:"Medicare beneficiary lookup", url:"https://portal.cms.gov/portal", color:C.header },
              { label:"🔍 UHC Jarvis", sub:"Medicaid verification", url:"https://www.uhcjarvis.com/content/jarvis/en/sign_in.html", color:"#0072CE" },
            ].map(p=>(
              <div key={p.label} onClick={()=>{ if(p.copyEmail && agent.email) navigator.clipboard.writeText(agent.email); window.open(p.url,"_blank"); }}
                style={{ background:"#ffffff", border:`2px solid ${p.color}`, borderRadius:10, padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div>
                  {p.img
                    ? <img src={p.img} alt="CMS" style={{ height:16, marginBottom:2, filter:"brightness(0) saturate(100%) invert(17%) sepia(32%) saturate(744%) hue-rotate(182deg) brightness(94%) contrast(93%)" }} />
                    : <div style={{ fontSize:13, fontWeight:"bold", color:"#1a2e4a" }}>{p.label}</div>}
                  <div style={{ fontSize:10, color:C.mid, marginTop:2 }}>{p.copyEmail && agent.email ? "Email copied — just paste it" : p.sub}</div>
                </div>
                <div style={{ fontSize:18, color:p.color, fontWeight:"bold" }}>→</div>
              </div>
            ))}
          </div>

          {/* ── MEDICAID VERIFICATION ── */}
          <div style={{ fontSize:10, color:C.header, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:"bold" }}>Medicaid Verification Portals</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
            {[
              { label:"UHC — Jarvis", sub:"UnitedHealthcare", url:"https://www.uhcjarvis.com/content/jarvis/en/sign_in.html", color:"#0072CE" },
              { label:"Aetna — ThinkAgent", sub:"Aetna Medicare", url:"https://app.thinkagent.com/", color:"#7D2248" },
              { label:"Anthem — mProducer", sub:"Anthem / Elevance", url:"https://mproducer.anthem.com/mproducer/public/", color:"#DB0011" },
              { label:"Cigna / Health Spring", sub:"Cigna Medicare", url:"https://cignahealthcare2.destinationrx.com/PC/Agent/Account/Login", color:"#006DB7" },
              { label:"Devoted Health", sub:"Devoted Agent Portal", url:"https://agent.devoted.com/", color:"#00A499" },
              { label:"AmeriHealth", sub:"AmeriHealth Broker Portal", url:"https://amerihealthcaritasnext.softheon.com/broker/", color:"#004B87" },
              { label:"Wellcare", sub:"Wellcare Broker Portal", url:"https://provider.wellcare.com/", color:"#E31837" },
            ].map(p=>(
              <div key={p.label} onClick={()=>window.open(p.url,"_blank")}
                style={{ border:`1px solid ${p.color}`, borderRadius:9, padding:"11px 12px", cursor:"pointer", background:"#ffffff" }}>
                <div style={{ fontSize:13, fontWeight:"bold", color:"#1a2e4a" }}>{p.label}</div>
                <div style={{ fontSize:10, color:C.mid, marginTop:2 }}>{p.sub}</div>
                <div style={{ fontSize:10, color:p.color, marginTop:4, fontWeight:"bold" }}>Tap to open →</div>
              </div>
            ))}
          </div>

          {/* ── PROVIDER / DOCTOR LOOKUP ── */}
          <div style={{ fontSize:10, color:C.header, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:"bold" }}>Provider / Doctor Lookup Portals</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
            {[
              { label:"UHC Provider Search", sub:"UnitedHealthcare", url:"https://find-a-doctor.uhc.com/", color:"#0072CE" },
              { label:"Humana Find a Doctor", sub:"Humana", url:"https://www.humana.com/medicare/find-a-doctor", color:"#007B40" },
              { label:"Aetna Provider Search", sub:"Aetna Medicare", url:"https://www.aetnamedicare.com/en/find-a-doctor.html", color:"#7D2248" },
              { label:"Anthem Provider Search", sub:"Anthem / Elevance", url:"https://www.anthem.com/find-doctor", color:"#DB0011" },
              { label:"Devoted Find Care", sub:"Devoted Health", url:"https://my.devoted.com", color:"#00A499" },
              { label:"AmeriHealth Provider", sub:"AmeriHealth", url:"https://www.amerihealth.com/medicare", color:"#004B87" },
              { label:"Cigna / Health Spring", sub:"Cigna Medicare", url:"https://hcpdirectory.cigna.com/", color:"#006DB7" },
              { label:"Wellcare Provider Search", sub:"Wellcare", url:"https://provider.wellcare.com", color:"#E31837" },
              { label:"Mutual of Omaha", sub:"Mutual of Omaha Medicare", url:"https://www.mutualofomaha.com/medicare", color:"#003087" },
            ].map(p=>(
              <div key={p.label} onClick={()=>window.open(p.url,"_blank")}
                style={{ background:`rgba(${p.color==="#0072CE"?"0,114,206":p.color==="#007B40"?"0,123,64":p.color==="#7D2248"?"125,34,72":p.color==="#DB0011"?"219,0,17":p.color==="#00A499"?"0,164,153":p.color==="#006DB7"?"0,109,183":"0,75,135"},0.07)`, border:`1px solid ${p.color}`, borderRadius:9, padding:"11px 12px", cursor:"pointer" }}>
                <div style={{ fontSize:13, fontWeight:"bold", color:"#1a2e4a" }}>{p.label}</div>
                <div style={{ fontSize:10, color:C.mid, marginTop:2 }}>{p.sub}</div>
                <div style={{ fontSize:10, color:p.color, marginTop:4, fontWeight:"bold" }}>🔍 Tap to open →</div>
              </div>
            ))}
          </div>

          {/* ── CROSS-SELL PORTALS ── */}
          <div style={{ fontSize:10, color:C.header, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontWeight:"bold" }}>Cross-Sell Portals (Post-TPV)</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {[
              { label:"UHOne", sub:"UnitedHealthcare One — HIP", url:"https://www.uhone.com", color:"#0072CE" },
              { label:"UL Insurance", sub:"Hospital Indemnity", url:"https://www.ulinsurance.com", color:"#1a2e4a" },
              { label:"Heartland National", sub:"Hospital Indemnity", url:"https://www.heartlandnational.com/agent-login", color:"#C8102E" },
              { label:"American Continental", sub:"Aetna — Hospital Indemnity", url:"https://www.americancontinental.com/login", color:"#7D2248" },
            ].map(p=>(
              <div key={p.label} onClick={()=>window.open(p.url,"_blank")}
                style={{ background:`rgba(0,0,0,0.03)`, border:`1px solid ${p.color||C.teal}`, borderRadius:9, padding:"11px 12px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:"bold", color:"#1a2e4a" }}>{p.label}</div>
                  <div style={{ fontSize:10, color:C.mid, marginTop:2 }}>{p.sub}</div>
                </div>
                <div style={{ fontSize:16, color:p.color||C.teal, fontWeight:"bold" }}>→</div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── 2. OPENER & RECORDED LINE ── */}
        <Card>
          <SecHead num={2} icon="🎙" title="Opening & Recorded Line" done={sec.opener} />
          <Script>"How can I help you today?"</Script>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <div style={{ flex:1, textAlign:"center", padding:"10px 6px", borderRadius:7, border:`1px solid ${sec.opener==="yes"?C.green:C.blue}`, background:sec.opener==="yes"?C.greenBg:"transparent", cursor:"pointer", fontSize:13, color:sec.opener==="yes"?C.green:C.mid }} onClick={()=>setSec(p=>({...p,opener:"yes"}))}>✓ YES — Has Medicare A & B</div>
            <div style={{ flex:1, textAlign:"center", padding:"10px 6px", borderRadius:7, border:`1px solid ${sec.opener==="no"?C.red:C.blue}`, background:sec.opener==="no"?C.redBg:"transparent", cursor:"pointer", fontSize:13, color:sec.opener==="no"?C.red:C.mid }} onClick={()=>setSec(p=>({...p,opener:"no"}))}>✗ NO — Does not qualify</div>
          </div>
          <Script>"Great! You called in at the perfect time of year! Are there any specific benefits you are looking for in 2026?"</Script>
          <F lbl="Purpose of Call" value={bene.purposeOfCall} onChange={v=>ub("purposeOfCall",v)} placeholder="Why are they calling / what are they looking for in 2026?" />
          <Script>"Ok, we can definitely look into that for you."</Script>
          <F lbl="The Why for the Call" value={bene.theWhy} onChange={v=>ub("theWhy",v)} placeholder="Their core reason / pain point" />
        </Card>

        {/* ── 3. ZIP CODE VERIFICATION ── */}
        <Card>
          <SecHead num={3} icon="📍" title="Zip Code Verification" done={sec.zipName} />
          <Note>Enter the zip code in Sunfire and click VIEW ALL PLANS</Note>
          <Script>"Can you confirm your zip code for me please? Thank you. And who do I have the pleasure of speaking with?"</Script>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <F lbl="Zip Code" value={bene.zip} onChange={v=>ub("zip",v)} half />
            <F lbl="First Name" value={bene.firstName} onChange={v=>ub("firstName",v)} half />
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <F lbl="Last Name" value={bene.lastName} onChange={v=>ub("lastName",v)} half />
            <F lbl="Phone" value={bene.phone} onChange={v=>ub("phone",v)} type="tel" half />
          </div>
          <F lbl="Date of Birth" value={bene.dob} onChange={v=>ub("dob",v)} type="date" />
          <Chk checked={!!sec.zipName} onClick={()=>done("zipName")}>Zip confirmed in Sunfire — name and contact captured ✓</Chk>
        </Card>

        {/* ── 4. REQUIRED DISCLAIMERS ── */}
        <Card>
          <SecHead num={4} icon="📋" title="Required Disclaimers" done={sec.disclaimer} />
          <Script>"Before I can look at what you are eligible for through Medicare, I'm required to read a quick disclaimer..."</Script>
          <Note>PLAY RECORDING ON ENROLL HERE — Medicare Disclaimer (read verbatim)</Note>
          <div style={{ background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, padding:"12px 14px", fontSize:12, color:C.text, lineHeight:1.8, marginBottom:10 }}>
            <strong style={{color:C.warn}}>MEDICARE DISCLAIMER (Verbatim):</strong><br/>
            "The plans available today are limited to those we offer in your area — we do not offer every plan available, and the number of organizations and products we represent varies by ZIP code. For a complete list of all available options, please contact Medicare.gov or call 1-800-MEDICARE. You will not be required to provide any health-related information unless it is necessary to determine enrollment eligibility. Please also be aware that for your protection, this call may be monitored and recorded."
          </div>
          <Chk checked={!!sec.disclaimer} onClick={()=>done("disclaimer")}>Medicare disclaimer played via Enroll Here recording ✓</Chk>
        </Card>

        {/* ── 5. SCOPE OF APPOINTMENT ── */}
        <Card>
          <SecHead num={5} icon="📝" title="Scope of Appointment" done={sec.soa} />
          <Note>PLAY RECORDING ON ENROLL HERE — Scope of Appointment (read verbatim)</Note>
          <div style={{ background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, padding:"12px 14px", fontSize:12, color:C.text, lineHeight:1.8, marginBottom:10 }}>
            <strong style={{color:C.warn}}>SCOPE OF APPOINTMENT (Verbatim):</strong><br/>
            "Today's discussion will be limited to Medicare Advantage and ancillary plans only. Agreeing to move forward does not obligate you to enroll in anything, and your current health coverage will not be affected unless you choose to enroll in a plan today. By continuing, you are also agreeing that {agent.company||"our agency"} and its representatives may reach out to you by phone, email, or text regarding these plan types now and in the future — you may opt out at any time by simply saying 'stop' or asking us to end contact. Do I have your permission to proceed?"
          </div>
          <Script>"Did you get a clear yes? Do you have MEDICAID as well as MEDICARE?"</Script>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <div style={{ flex:1, textAlign:"center", padding:"10px 6px", borderRadius:7, border:`1px solid ${sec.soaYes?C.green:C.blue}`, background:sec.soaYes?C.greenBg:"transparent", cursor:"pointer", fontSize:13, color:sec.soaYes?C.green:C.mid }} onClick={()=>setSec(p=>({...p,soaYes:true}))}>✓ Clear YES Obtained</div>
          </div>
          <Script>"Great, are you looking for benefits just for you or for you and someone else?"</Script>
          <Sel lbl="Benefits For" value={bene.benefitsFor} onChange={v=>ub("benefitsFor",v)} options={["Just Me","Me and Someone Else"]} />
          <Script>"Do you make your own healthcare decisions?"</Script>
          <Sel lbl="Is beneficiary making their own decisions?" value={bene.ownDecisions||"Yes"} onChange={v=>ub("ownDecisions",v)} options={["Yes — Beneficiary making own decisions","No — Power of Attorney on the call"]} />
          {bene.ownDecisions==="No — Power of Attorney on the call" && (
            <div style={{background:"rgba(192,0,0,0.06)",border:"1px solid #c00000",borderRadius:8,padding:"12px 14px",marginBottom:10}}>
              <div style={{fontSize:12,color:"#c00000",fontWeight:"bold",marginBottom:6}}>🔴 POA ON THE CALL — Read Verbatim:</div>
              <div style={{fontSize:13,color:"#c00000",lineHeight:1.8,fontWeight:"bold"}}>
                "I want to make you aware that as Power of Attorney, you are authorized to make this Medicare Advantage enrollment decision on behalf of [beneficiary name]. Please be advised that Medicare and/or the plan carrier may contact you at any time to verify your Power of Attorney documentation and authority to act on the beneficiary's behalf. You may be required to provide a copy of the POA document at that time."
              </div>
            </div>
          )}
          <Script>"Do you have any employer, VA benefits, TRICARE or ChampVA benefits?"</Script>
          <Sel lbl="Other Benefits" value={bene.otherBenefits} onChange={v=>ub("otherBenefits",v)} options={["No","Yes - VA","Yes - TRICARE","Yes - ChampVA","Yes - Employer"]} />
          <Script>"Do you have any Chronic Health Conditions that require special care like Diabetes or heart disease?"</Script>
          <Sel lbl="CSNP Eligible?" value={bene.csnp} onChange={v=>ub("csnp",v)} options={["No","Yes"]} />
          {bene.csnp==="Yes" && <>
            <F lbl="If Yes — What Condition?" value={bene.csnpCondition} onChange={v=>ub("csnpCondition",v)} placeholder="e.g. Diabetes, Heart Disease" />
            <Warn>CSNP ONLY — Verify they have seen the diagnosing provider in the last year. If not, the plan will cancel due to no provider contact being made to verify the chronic condition.</Warn>
          </>}
          <Chk checked={!!sec.soa} onClick={()=>done("soa")}>SOA played — clear YES obtained — all qualifying questions answered ✓</Chk>
        </Card>

        {/* ── 6. MARX CHECK / MBI ── */}
        <Card>
          <SecHead num={6} icon="🪪" title="MARx Check — Obtain MBI" done={sec.marxCheck} />
          <Script>"Grab your Red White and Blue Medicare card so we can see what you are eligible for." [Pause and wait to gather card information.]</Script>
          <Script>"Do I have permission to look you up in the Medicare system?" (Obtain a clear Yes/No)</Script>
          <F lbl="Medicare # (MBI) *" value={bene.mbi} onChange={v=>ub("mbi",v)} placeholder="1EG4-TE5-MK72" />
          <Note>Enter Member MBI, DOB and Zip code into Sunfire to pull up their plan, LIS Level, Medicaid Eligibility, Doctors and Medications</Note>
          <Script>(Verbally confirm the client's address with MARx Report info)</Script>
          <ImgUpload label="Attach Screenshot of MARx" value={screenshots.marx} onChange={v=>setScreenshots(p=>({...p,marx:v}))} />
          <Script>"If Member has Medicaid obtain Medicaid number: Can you verify your Medicaid # for me?" (Verify Medicaid level in the correct carrier portal)</Script>
          <Sel lbl="Has Medicaid?" value={bene.hasMedicaid} onChange={v=>ub("hasMedicaid",v)} options={["No","Yes"]} />
          {bene.hasMedicaid==="Yes" && <>
            <F lbl="Medicaid # — YES OR NO IF YES INSERT #" value={bene.medicaidNum} onChange={v=>ub("medicaidNum",v)} />
            <Sel lbl="Medicaid Level & Confirm Screenshot" value={bene.medicaidLevel} onChange={v=>ub("medicaidLevel",v)} options={["None","Level 1 (Full LIS)","Level 2","Level 3","Level 4 (Partial LIS)"]} />
          </>}
          <Script>"If Medicare card is obtained: Begin a quick comparison for standout benefits after looking up their current plan."</Script>
          <Script>"If client doesn't have Medicare Card: I can pull up your Medicare number with your DOB and your social."</Script>
          <Chk checked={!!sec.marxCheck} onClick={()=>done("marxCheck")}>MBI obtained — MARx verified — Medicaid confirmed ✓</Chk>
        </Card>

        {/* ── 7. NEEDS ANALYSIS ── */}
        <Card>
          <SecHead num={7} icon="❤️" title="Needs Analysis" done={sec.needsAnalysis} />
          <Script>"Before we look at plans, I want to make sure we find the right fit for you. Can you tell me a little about your health — are you currently seeing any doctors regularly, managing any ongoing conditions, or taking any medications?"</Script>
          <Warn>Do not move on from the needs analysis until your member feels the pain point. If only you feel the pain point, only you will buy in to the new plan.</Warn>
          <div style={{ marginBottom:14 }}>
            {[
              { need:"🏥 Healthcare Needs", script:"Outside of your regular doctor visits, are there any upcoming procedures, specialist visits, or medical needs you are planning for this year?" },
              { need:"🦷 Dental", script:"When was the last time you visited your dentist? What was done during that visit and how much did it cost you out of pocket? Do you have any upcoming dental work planned?" },
              { need:"👓 Vision", script:"When did you last get your eyes checked or pick up a new pair of glasses or contacts? How much did that run you? Is that something you expect to need again soon?" },
              { need:"💊 Prescriptions", script:"Can you walk me through the medications you are currently taking along with the dosages? I want to make sure they are covered and as cost effective as possible for you." },
              { need:"🛒 OTC / Drugstore", script:"How often are you buying things like vitamins, over-the-counter pain relievers, or personal care items? About how much do you spend on those each month?" },
              { need:"🚗 Transportation", script:"How are you getting to your medical appointments right now? Is transportation ever a challenge or an added expense for you?" },
            ].map(item=>(
              <div key={item.need} style={{ background:"#ffffff", border:`2px solid ${C.tealBorder}`, borderLeft:`5px solid ${C.teal}`, borderRadius:10, padding:"14px 16px", marginBottom:10, boxShadow:"0 2px 8px rgba(58,125,156,0.08)" }}>
                <div style={{ fontSize: isDesktop ? 16 : 13, color:C.header, fontWeight:"bold", letterSpacing:0.5, marginBottom:8 }}>{item.need}</div>
                <div style={{ fontSize:14, color:"#1a2e4a", lineHeight:1.75, fontWeight:"600", letterSpacing:0.2 }}>
                  "{item.script}"
                </div>
              </div>
            ))}
          </div>
          {/* Needs Grid */}
          <div style={{ marginBottom:12 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6, marginBottom:6 }}>
              <div style={{ fontSize:10, color:C.dim, textTransform:"uppercase" }}>Need</div>
              <div style={{ fontSize:10, color:C.red, textTransform:"uppercase", textAlign:"center" }}>Not Satisfied</div>
              <div style={{ fontSize:10, color:C.warn, textTransform:"uppercase", textAlign:"center" }}>Somewhat</div>
              <div style={{ fontSize:10, color:C.green, textTransform:"uppercase", textAlign:"center" }}>Satisfied</div>
            </div>
            {["Dental","Vision","Scripts","Transportation"].map(need=>(
              <div key={need} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6, marginBottom:6, alignItems:"center" }}>
                <div style={{ fontSize:13, color:C.text }}>{need}</div>
                {NEED_LEVELS.map(lvl=>(
                  <div key={lvl} onClick={()=>setNeeds(p=>({...p,[need]:lvl}))} style={{ textAlign:"center", padding:"7px 4px", borderRadius:6, border:`1px solid ${needs[need]===lvl?(lvl==="Not Satisfied"?C.red:lvl==="Somewhat Satisfied"?C.warn:C.green):C.blue}`, background:needs[need]===lvl?(lvl==="Not Satisfied"?C.redBg:lvl==="Somewhat Satisfied"?"rgba(224,170,58,0.1)":C.greenBg):"transparent", cursor:"pointer", fontSize:11, color:needs[need]===lvl?(lvl==="Not Satisfied"?C.red:lvl==="Somewhat Satisfied"?C.warn:C.green):C.dim }}>
                    {lvl==="Not Satisfied"?"✗":lvl==="Somewhat Satisfied"?"~":"✓"}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <Chk checked={!!sec.needsAnalysis} onClick={()=>done("needsAnalysis")}>Needs analysis complete — member felt pain point ✓</Chk>
        </Card>

        {/* ── 8. SAVINGS COMPARISON / PLAN PRESENTATION ── */}
        <Card>
          <SecHead num={8} icon="💰" title="Savings Comparison — Count the Money" done={sec.planPresent} />
          <Note>Search for plans with the primary benefit and any secondary benefit increases and pitch. Count the money in this section as needed.</Note>

          {/* ── STACK THE MONEY ── */}
          <div style={{ background:"#ffffff", border:`2px solid ${C.teal}`, borderRadius:12, padding:16, marginBottom:14, boxShadow:"0 3px 12px rgba(62,207,207,0.15)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
              <span style={{ fontSize:22 }}>💰</span>
              <span style={{ fontSize:15, fontWeight:"bold", color:C.header, letterSpacing:0.5 }}>Count the Money</span>
            </div>

            {/* Stack the Money — full calculator with frequency */}
            <StackCalculator plan={plan} up={up} />

            {/* Notes */}
            <div style={{ marginTop:12 }}>
              <div style={{ fontSize:11, color:C.mid, textTransform:"uppercase", letterSpacing:1, fontWeight:"bold", marginBottom:6 }}>Count Notes</div>
              <textarea
                value={plan.stackNotes||""}
                onChange={e=>up("stackNotes",e.target.value)}
                placeholder="Notes on the value comparison, what resonated with the client, pain points tied to benefits..."
                style={{ width:"100%", boxSizing:"border-box", background:"#fff", border:`1px solid ${C.border}`, borderRadius:8, color:"#1a2e4a", padding:"10px 12px", fontSize:13, minHeight:80, resize:"vertical", outline:"none", fontFamily:"'Helvetica Neue',Arial,sans-serif" }}
              />
            </div>
          </div>
          <Warn>YOU MUST verify Medicaid level in the carrier portal of the plan that you are pitching! (If DSNP)</Warn>
          <Script>"Great news! It looks like you are eligible for a 2026 Advantage plan with additional benefits in your area."</Script>
          <Script>"Got it, I found a 2026 plan that would fit the needs we just discussed!"</Script>
          <Note>Gain a soft buy-in — repeat the needs back to them, tie the benefits into their needs, and get their buy-in after each benefit. Count the money in this section as needed.</Note>
          <Script>"Now let's see if you qualify with an enrollment period" (skip if they already found an SEP)</Script>
          <Sel lbl="Special Enrollment Period (SEP)" value={plan.sepFound} onChange={v=>up("sepFound",v)} options={[
            "— Select SEP —",
            "AEP — Annual Enrollment Period (Oct 15 - Dec 7)",
            "MOV — Moved to a new service area",
            "5ST — Changing to 5-Star plan",
            "ACC — Carrier or CMS unable to provide required notices in accessible format",
            "CSN — Found ineligible for CSNP plan, want to enroll in another plan",
            "CSP — Enrolled in Medicare premium Part A or Part B using Exceptional Condition",
            "DIF — Auto assigned by CMS or State",
            "ICE — Delayed Part B",
            "INC — Released from incarceration",
            "LAW — Non-U.S. citizen who became lawfully present in the United States",
            "LCC — Lost creditable coverage",
            "LPI — In a plan with less than 3 stars for 3 years, joining 3-star or higher plan",
            "LT2 — Live in a long-term care facility like a nursing home or rehabilitation hospital",
            "LTC — Moved out of an institution",
            "MCD — Change in Medicaid Status",
            "MYT — Current plan is not renewing",
            "NLS — Gain, Lose or Change in LIS-Eligible Status",
            "OTH — Other",
            "PAC — Enrolling in Program of All-inclusive Care for the Elderly (PACE)",
            "PAP — State Pharmaceutical Assistance Programs (SPAP) member",
            "PRE — Enrolled in Part B during Part B General Enrollment Period when NOT entitled to free Part A",
            "REC — Plan recently taken over by the state due to financial issues",
            "RET — New to Medicare, notified about getting Medicare after Part A and/or Part B coverage started",
            "RUS — Moved back to the U.S. after living abroad",
            "No SEP Found — Notify Manager Before Ending Call",
          ]} />
          {plan.sepFound.includes("No SEP") && <Warn>No SEP found — Notify manager BEFORE ending call. Do NOT proceed with enrollment. Use the 🔴 Need Approval button at the top.</Warn>}
          {plan.sepFound.includes("No") && <Warn>No SEP found — Notify manager BEFORE ending call. Do NOT proceed with enrollment.</Warn>}
          <Script>"I can help get those benefits and help you enroll in <strong style={{color:C.gold}}>{plan.planName||"(plan name)"}</strong> today, for a ___ month ___ 1st start date!"</Script>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <F lbl="Plan Name" value={plan.planName} onChange={v=>up("planName",v)} placeholder="e.g. Humana Gold Plus H1234-001" half />
            <F lbl="Carrier" value={plan.carrier} onChange={v=>up("carrier",v)} placeholder="e.g. Humana, Aetna, UHC" half />
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <F lbl="Effective Start Date" value={plan.startDate} onChange={v=>up("startDate",v)} type="date" half />
            <Sel lbl="Plan Type" value={plan.planType} onChange={v=>up("planType",v)} options={["HMO","PPO","DSNP","CSNP"]} />
          </div>
          <F lbl="Current / Prior Carrier" value={plan.currentCarrier} onChange={v=>up("currentCarrier",v)} placeholder="e.g. Aetna Medicare Advantage" />
          {/* SSBCI Disclaimer */}
          <div style={{ background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, padding:"12px 14px", fontSize:12, color:C.text, lineHeight:1.8, marginBottom:10 }}>
            <strong style={{color:C.warn}}>READ SSBCI DISCLAIMER VERBATIM:</strong><br/>
            "Some plans offer Special Supplemental Benefits for members with certain chronic conditions. To qualify, you must have an eligible chronic condition and meet the plan criteria. Eligibility is determined by the health plan after enrollment. Benefits are not guaranteed and are only available once eligibility is confirmed."
          </div>
          <Note>Only go over the benefits below if specifically discussed/mentioned on the call. Check SOB/EOC for details:</Note>
          <div style={{ fontSize:12, color:C.mid, lineHeight:2, marginBottom:12, background:"rgba(255,255,255,0.02)", border:`1px solid ${C.blue}`, borderRadius:8, padding:"10px 14px" }}>
            {["Hearing","Dental","Vision","Transportation","Silver Sneakers / Fitness","Nurses Hotline","Over the Counter (OTC) — Search for food, utility and rent benefits. Do NOT assume every OTC card covers food","Food Card — MUST READ SSBCI DISCLOSURE ABOVE","Aetna ONLY — High Value Provider benefit — Can only be used if PCP is a high value provider"].map((b,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"4px 0", borderBottom:`1px solid rgba(26,58,110,0.3)` }}>
                <span style={{ color:C.dim, fontSize:11 }}>•</span>
                <span>{b}</span>
              </div>
            ))}
          </div>
          {/* Plan Type Disclosures */}
          <Note>Confirm in summary of benefits if client needs referral for specialist and read relevant statement below:</Note>
          {(plan.planType==="HMO") && <>
            <Sel lbl="HMO — Referrals Required?" value={plan.referrals} onChange={v=>up("referrals",v)} options={["Required","Not Required"]} />
            {plan.referrals==="Required" && <Script>"This plan is an HMO plan which means you will generally have to see your primary care doctor first if you need to see a specialist. Your primary care provider acts as the gatekeeper and will coordinate care for you."</Script>}
            {plan.referrals==="Not Required" && <Script>"Although this plan is an HMO and you have a primary care provider assigned who will coordinate your care, with this plan referrals are not required to see an in-network specialist."</Script>}
          </>}
          {plan.planType==="PPO" && <Script>"This is a PPO plan, which allows you to see in-network specialists without needing a referral. PPO plans also cover providers that are out-of-network. If you go out of the network, make sure the doctor is going to accept the plan and is Medicare approved. Otherwise, you may have to pay the full price. Seeing providers in-network may keep costs lower."</Script>}
          <ImgUpload label="Attach Savings Comparison / Plan Screenshots" value={screenshots.planComparison} onChange={v=>setScreenshots(p=>({...p,planComparison:v}))} />
          <Chk checked={!!sec.planPresent} onClick={()=>done("planPresent")}>Plan presented — soft buy-in obtained — SSBCI read — plan type disclosed ✓</Chk>
        </Card>

        {/* ── 8B. REQUIRED BENEFITS DISCLOSURE ── */}
        <Card>
          <SecHead num="8B" icon="🔴" title="Advise of Required Benefits — MUST READ" done={sec.benefitsDisclosure} />
          <RedNote>ALL items below MUST be read to the beneficiary. Check each box as you complete it.</RedNote>

          {/* Plan Selector */}
          {(()=>{ const ri={ width:"100%", boxSizing:"border-box", background:"#fff", border:"1px solid #c00000", borderRadius:5, padding:"7px 10px", fontSize:13, color:"#c00000", fontWeight:"bold" }; return (
          <div style={{ background:"rgba(192,0,0,0.04)", border:"1px solid #c00000", borderRadius:8, padding:"12px 14px", marginBottom:12 }}>
            <div style={{ fontSize:11, color:"#c00000", letterSpacing:1.5, textTransform:"uppercase", fontWeight:"bold", marginBottom:10 }}>Step 1 — Select Carrier & Plan</div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
              <div style={{ flex:"1 1 160px" }}>
                <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>Carrier</div>
                <select value={plan.carrier} onChange={e=>{ up("carrier",e.target.value); up("planName",""); setPlanEditMode(false); }} style={ri}>
                  <option value="">— Select Carrier —</option>
                  {CARRIERS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex:"2 1 220px" }}>
                <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>Plan Name</div>
                <select value={plan.planName} onChange={e=>loadPlanFromLibrary(e.target.value)} disabled={!plan.carrier} style={ri}>
                  <option value="">— Select Plan —</option>
                  {planLibrary.filter(p=>p.carrier===plan.carrier).map(p=><option key={p.id} value={p.planName}>{p.planName}</option>)}
                </select>
              </div>
              <div style={{ display:"flex", alignItems:"flex-end", gap:6 }}>
                <button onClick={()=>{ up("planName",""); PLAN_BENEFIT_FIELDS.forEach(k=>up(k,"")); setPlanEditMode(true); }} style={{ background:"#1a2e4a", border:"none", borderRadius:5, color:"#fff", padding:"8px 12px", fontSize:11, cursor:"pointer", fontWeight:"bold", whiteSpace:"nowrap" }}>+ New Plan</button>
                {plan.planName && <button onClick={()=>setPlanEditMode(m=>!m)} style={{ background:planEditMode?"#888":"#c00000", border:"none", borderRadius:5, color:"#fff", padding:"8px 12px", fontSize:11, cursor:"pointer", fontWeight:"bold" }}>{planEditMode?"Cancel":"✏️ Edit"}</button>}
              </div>
            </div>
            {planEditMode && (
              <div style={{ borderTop:"1px solid rgba(192,0,0,0.2)", paddingTop:12 }}>
                <div style={{ fontSize:10, color:"#c00000", fontWeight:"bold", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Plan Details</div>
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>Plan Name</div>
                  <input value={plan.planName} onChange={e=>up("planName",e.target.value)} placeholder="Full plan name e.g. Humana Gold Plus H1036-054C" style={ri} />
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {[["Plan Premium","premium","$ per month"],["Part B Giveback","partBGiveback","$ or N/A"],["Max Out of Pocket","moop","$"],["Medical Deductible","deductible","$"],["Inpatient Hospital Copay","inpatientCopay","$ per day / days"],["PCP Copay","pcpCopay","$"],["Specialist Copay","specialistCopay","$"],["ER / Urgent Care","erCopay","$"],["Mental Health Copay","mentalHealthCopay","$"],["Rx Deductible","rxDeductible","$"],["Pharmacy Network","pharmacyNetwork","pharmacy name"]].map(([lbl,key,ph])=>(
                    <div key={key} style={{ flex:"1 1 42%", marginBottom:6 }}>
                      <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>{lbl}</div>
                      <input value={plan[key]||""} onChange={e=>up(key,e.target.value)} placeholder={ph} style={ri} />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:10, color:"#c00000", fontWeight:"bold", textTransform:"uppercase", letterSpacing:1, margin:"10px 0 6px" }}>Drug Tier Copays</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                  {[1,2,3,4,5].map(t=>(
                    <div key={t} style={{ flex:"1 1 80px" }}>
                      <div style={{ fontSize:9, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>Tier {t}</div>
                      <input value={plan[`tier${t}Copay`]||""} onChange={e=>up(`tier${t}Copay`,e.target.value)} placeholder="$0" style={ri} />
                    </div>
                  ))}
                </div>
                <button onClick={savePlanToLibrary} disabled={planSaving||!plan.carrier||!plan.planName} style={{ background:planSaving?"#ccc":"#c00000", border:"none", borderRadius:5, color:"#fff", padding:"9px 18px", fontSize:12, cursor:planSaving?"not-allowed":"pointer", fontWeight:"bold" }}>
                  {planSaving ? "Saving..." : "💾 Save Plan to Library"}
                </button>
              </div>
            )}
          </div>
          ); })()}

          {/* Read Each Item & Check */}
          <div style={{ fontSize:11, color:"#c00000", letterSpacing:1.5, textTransform:"uppercase", fontWeight:"bold", marginBottom:8 }}>Step 2 — Read Each Item & Check When Done</div>

          {[
            { id:"b_planname", text:`Plan Name & Effective Date & Plan Type — "${plan.planName||"[Plan Name]"}, effective ${plan.startDate||"[Date]"}, ${plan.planType} plan" (Must disclose full name of plan. Example: Humana Gold Plus H1036-054C HMO)` },
            { id:"b_premium", text:`The plan premium is $${plan.premium||"___"} per month` },
            { id:"b_giveback", text:`Part B Premium Giveback is $${plan.partBGiveback||"___"} (if applicable)` },
            { id:"b_moop", text:`The Maximum Out of Pocket is $${plan.moop||"___"}` },
            { id:"b_deductible", text:`Medical Deductible is $${plan.deductible||"___"}` },
            { id:"b_inpatient", text:`The Inpatient/Outpatient Hospital Copay (in/out of network) is $${plan.inpatientCopay||"___"} per day for the first days` },
            { id:"b_pcpcopay", text:`The PCP Copay (in/out of network) is $${plan.pcpCopay||"___"}` },
            { id:"b_preventive", text:`"This plan covers your preventative care. You can get your annual wellness visit including any lab work, flu vaccine and other preventative services at no cost." — READ VERBATIM` },
            { id:"b_specialist", text:`The Specialist Copay is $${plan.specialistCopay||"___"}` },
            { id:"b_er", text:`The ER/Urgent Care Copay is $${plan.erCopay||"___"}` },
            { id:"b_mental", text:`The Mental Health inpatient/outpatient Copay (in/out of network) is $${plan.mentalHealthCopay||"___"}` },
            { id:"b_network", text:`Provider Network Status (PCP) — State PCP Full Name: ${plan.pcpName||"[PCP]"}, Address, PCP ID: ${plan.pcpId||"[ID]"} & advise if PCP is in/out of network` },
            { id:"b_tiers", text:`Current Drug Tiers & Restrictions (Prior Authorization, Quantity Limits, Step Therapy) — Read from formulary` },
            { id:"b_priorauth", text:`MUST READ: "Certain medications have prior authorization requirements; all you have to do is call the prescribing doctor and request a prior authorization to be sent to ${plan.carrier||"[carrier]"} if that is the case."` },
            { id:"b_rxded", text:`Prescription Deductible, Initial Coverage/Tiers, Catastrophic Coverage copays — Read directly from the Summary of Benefits` },
            { id:"b_pharmacy", text:`Pharmacy Network Status — ${plan.pharmacyNetwork||"[State pharmacy network]"}` },
            { id:"b_ssbci", text:`Read SSBCI Disclaimer Verbatim: "Some plans offer Special Supplemental Benefits for members with certain chronic conditions. To qualify, you must have an eligible chronic condition and meet the plan criteria. Eligibility is determined by the health plan after enrollment. Benefits are not guaranteed and are only available once eligibility is confirmed."` },
          ].map(item=>(
            <div key={item.id} onClick={()=>setSec(p=>({...p,[item.id]:!p[item.id]}))}
              style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", padding:"9px 0", borderBottom:"1px solid rgba(192,0,0,0.2)" }}>
              <div style={{ width:22, height:22, borderRadius:5, border:sec[item.id]?"2px solid #2a9a6a":"2px solid #c00000", background:sec[item.id]?"#2a9a6a":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:12, color:"#fff", fontWeight:"bold", marginTop:1 }}>{sec[item.id]?"✓":""}</div>
              <span style={{ fontSize:12, color:"#c00000", lineHeight:1.5, fontWeight:"600" }}>{item.text}</span>
            </div>
          ))}

          <div style={{ marginTop:12 }}>
            <Chk checked={!!sec.benefitsDisclosure} onClick={()=>done("benefitsDisclosure")}>All required benefits disclosed — all boxes checked above ✓</Chk>
          </div>
        </Card>

        {/* ── 9. PROVIDERS LOOKUP ── */}
        <Card>
          <SecHead num={9} icon="🏥" title="Providers Lookup" done={sec.providers} />
          <Warn>Agent MUST have guest provider carrier site open and ready to search BEFORE asking about providers.</Warn>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:C.header, fontWeight:"bold", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>🔍 Quick Provider Lookup — Tap to Open</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {[
                { label:"UHC", url:"https://find-a-doctor.uhc.com/", color:"#0072CE" },
                { label:"Humana", url:"https://www.humana.com/medicare/find-a-doctor", color:"#007B40" },
                { label:"Aetna", url:"https://www.aetnamedicare.com/en/find-a-doctor.html", color:"#7D2248" },
                { label:"Anthem", url:"https://www.anthem.com/find-doctor", color:"#DB0011" },
                { label:"Devoted", url:"https://my.devoted.com", color:"#00A499" },
                { label:"AmeriHealth", url:"https://www.amerihealth.com/medicare", color:"#004B87" },
                { label:"Cigna / Health Spring", url:"https://hcpdirectory.cigna.com/", color:"#006DB7" },
                { label:"Wellcare", url:"https://provider.wellcare.com", color:"#E31837" },
                { label:"Mutual of Omaha", url:"https://www.mutualofomaha.com/medicare", color:"#003087" },
              ].map(p=>(
                <div key={p.label} onClick={()=>window.open(p.url,"_blank")}
                  style={{ background:"#ffffff", border:`2px solid ${p.color}`, borderRadius:8, padding:"9px 12px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span style={{ fontSize:13, fontWeight:"bold", color:"#1a2e4a" }}>{p.label}</span>
                  <span style={{ fontSize:12, color:p.color, fontWeight:"bold" }}>→</span>
                </div>
              ))}
            </div>
          </div>
          <Script>"To confirm that your current providers are in network for this plan, can you tell me the name of your primary care physician?"</Script>
          <F lbl="Primary Doctor *" value={plan.pcpName} onChange={v=>up("pcpName",v)} placeholder="First Last" />
          <F lbl="PCP ID # *" value={plan.pcpId} onChange={v=>up("pcpId",v)} placeholder="Provider ID number" />
          <Script>"Can you tell me the name of any other doctors or specialists or facilities you visit so I can ensure they will be in network along with these benefits?"</Script>
          <F lbl="List All Specialists / Notes" value={plan.specialists} onChange={v=>up("specialists",v)} ta placeholder="List specialists, facilities, and in/out of network status..." />
          <F lbl="PCP Phone Number" value={plan.pcpPhone} onChange={v=>up("pcpPhone",v)} type="tel" />
          {(plan.carrier==="Cigna / Health Spring"||plan.carrier==="Devoted Health") && (
            <F lbl="PCP Fax Number" value={plan.pcpFax||""} onChange={v=>up("pcpFax",v)} type="tel" placeholder="Required for this carrier" />
          )}
          <Note>Important: This information is crucial to properly communicate to the beneficiary whether their PCP is in or out of network. If out of network, explain they will need to choose new ones or pay out of pocket.</Note>
          <Warn>Must assign PCP for every plan type (including PPO) and disclose PCP first and last name, PCP ID and phone number.</Warn>
          <Chk checked={!!sec.providers} onClick={()=>done("providers")}>Providers verified — PCP assigned and disclosed — in/out of network communicated ✓</Chk>
        </Card>

        {/* ── 10. MEDICATIONS ── */}
        <Card>
          <SecHead num={10} icon="💊" title="Prescription Drugs" done={sec.medsSection} />
          <Note>Agent should have the formulary up and ready to search. If in Sunfire verify first and ask if there are any additional meds.</Note>
          <Script>"Can you tell me what Medications you are currently taking as well as their dosage? I want to ensure they are covered and cost effective."</Script>
          <Script>"Is there a pharmacy you normally use to get your medications or do you get them via mail order?"</Script>
          <Note>If they refuse lookup — say: "I understand, we do not need to look up your medications. Just be aware that if a medication is not on the formulary, it will not be paid for by Medicare or the plan without authorization from a doctor. If your pharmacy is not in network, you may have to pay more for prescriptions."</Note>
          {bene.csnp==="Yes" && <Warn>CSNP ONLY — Verify they have seen the diagnosing provider in the last year. If not, the plan will cancel due to no provider contact being made to verify the chronic condition.</Warn>}
          <Note>Important Note: If assigning new PCP, you must provide PCP name and phone number.</Note>
          {/* 20-row medication table */}
          <div style={{ display:"grid", gridTemplateColumns:"2.2fr 1fr 0.8fr 0.6fr 0.7fr", gap:5, marginBottom:6 }}>
            {["Name of Medication","Dosage","Frequency (per day)","Tier #","Copay"].map(h=>(
              <div key={h} style={{ fontSize:10, color:"#3a7d9c", textTransform:"uppercase", fontWeight:"bold", lineHeight:1.3 }}>{h}</div>
            ))}
          </div>
          {meds.map((med,i)=>(
            <div key={i} style={{ marginBottom:7 }}>
              <div style={{ display:"grid", gridTemplateColumns:"2.2fr 1fr 0.8fr 0.6fr 0.7fr", gap:5, marginBottom:3 }}>
                {["name","dosage","frequency","tier","copay"].map(f=>(
                  <input key={f} value={med[f]} onChange={e=>{
                    const u=[...meds]; u[i]={...u[i],[f]:e.target.value};
                    if(f==="tier"){
                      const tierKey=`tier${e.target.value}Copay`;
                      if(plan[tierKey]) u[i]={...u[i],copay:plan[tierKey]};
                    }
                    setMeds(u);
                  }}
                    style={{ background: f==="tier" && med[f] ? "rgba(42,154,106,0.08)" : "#fff", border:`1px solid ${f==="tier" && med[f] ? C.green : C.border}`, borderRadius:5, color: f==="tier" && med[f] ? C.green : C.text, padding:"6px 7px", fontSize:12, width:"100%", boxSizing:"border-box", fontWeight: f==="tier" && med[f] ? "bold" : "normal" }}
                    placeholder={f==="name"?`Medication ${i+1}`:f==="tier"?"1-5":""} />
                ))}
              </div>
              {med && med.name && !med.tier && (
                <MedTierLookup
                  drugName={med.name||""}
                  planType={plan.planType||"HMO"}
                  onResult={tier=>{ try { const u=[...meds]; u[i]={...u[i],tier}; setMeds(u); } catch(e){} }}
                />
              )}
              {med.tier && (
                <div style={{ fontSize:10, color:C.green, marginLeft:2 }}>✓ Tier {med.tier}{plan[`tier${med.tier}Copay`] ? ` — Copay: ${plan[`tier${med.tier}Copay`]}` : " — enter copay"}</div>
              )}
            </div>
          ))}
          <div style={{ fontSize:11, color:C.dim, marginTop:4, marginBottom:8 }}>Rows 1–20 available above</div>
          <F lbl="CSNP or Other Notes" value={bene.csnpNotes||""} onChange={v=>ub("csnpNotes",v)} ta placeholder="Any CSNP notes, pharmacy preference, or additional medication notes..." />
          <Chk checked={!!sec.medsSection} onClick={()=>done("medsSection")}>Medications verified on formulary — pharmacy preference noted ✓</Chk>
        </Card>

        {/* ── 11. ADDRESS CONFIRMATION ── */}
        <Card>
          <SecHead num={11} icon="📬" title="Mailing Address Confirmation" done={sec.address} />
          <Script>"Perfect! Can you verify your mailing address is the same as your physical address?"</Script>
          <Sel lbl="Mailing Address same as Physical?" value={bene.mailingDiff?"No — Different":"Yes — Same"} onChange={v=>ub("mailingDiff",v==="No — Different")} options={["Yes — Same","No — Different"]} />
          <F lbl="Street Address" value={bene.street} onChange={v=>ub("street",v)} />
          <F lbl="Street Address Line 2 (optional)" value={bene.street2} onChange={v=>ub("street2",v)} />
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <F lbl="City" value={bene.city} onChange={v=>ub("city",v)} half />
            <F lbl="State" value={bene.state} onChange={v=>ub("state",v)} half />
          </div>
          <F lbl="Postal / Zip Code" value={bene.zip} onChange={v=>ub("zip",v)} />
          {bene.mailingDiff && <>
            <div style={{ fontSize:11, color:C.gold, marginBottom:8 }}>Different Mailing Address:</div>
            <F lbl="Mailing Street" value={bene.mailStreet} onChange={v=>ub("mailStreet",v)} />
            <div style={{ display:"flex", gap:10 }}>
              <F lbl="City" value={bene.mailCity} onChange={v=>ub("mailCity",v)} half />
              <F lbl="State / Zip" value={bene.mailState} onChange={v=>ub("mailState",v)} half />
            </div>
          </>}
          <F lbl="Email Address (optional)" value={bene.email} onChange={v=>ub("email",v)} type="email" />
          <Chk checked={!!sec.address} onClick={()=>done("address")}>Address verified and confirmed ✓</Chk>
        </Card>

        {/* ── 12. FINAL ENROLLMENT CONFIRMATION ── */}
        <Card>
          <SecHead num={12} icon="✅" title="Final Enrollment Confirmation" done={sec.finalConfirm} />
          <Script>"Perfect! So Mr./Mrs. <strong style={{color:C.gold}}>{fullName||"____"}</strong> we are going to begin the application for the plan we just discussed. Just to be clear you want to enroll into the <strong style={{color:C.gold}}>{plan.planName||"(New plan name)"}</strong> that will start on <strong style={{color:C.gold}}>{plan.startDate||"(Date)"}</strong>, and the day it goes into effect, it will be taking the place of your existing <strong style={{color:C.gold}}>{plan.currentCarrier||"(current carrier)"}</strong> plan. Correct?"</Script>
          <ImgUpload label="Attach Enrollment Screenshots" value={screenshots.enrollment} onChange={v=>setScreenshots(p=>({...p,enrollment:v}))} />
          <Chk checked={!!sec.finalConfirm} onClick={()=>done("finalConfirm")}>Final verbal confirmation obtained from beneficiary ✓</Chk>
        </Card>

        {/* ── 13. POST SUNFIRE SUBMISSION SCRIPT ── */}
        <Card>
          <SecHead num={13} icon="🎉" title="After Sunfire Application Submitted" done={sec.postSubmit} />
          <RedNote>Read ONLY after Sunfire enrollment is submitted.</RedNote>
          <MustRead>"Your application is submitted. Your confirmation number is <strong>[read enrollment ID from Sunfire]</strong> and your effective date is <strong>{plan.startDate||"[first of next month]"}</strong>. You'll receive a text in the next few seconds — please open it when it comes in."</MustRead>
          <RedNote>Wait until the member confirms they have the text open — then continue.</RedNote>
          <MustRead>"That text has a link to save my contact card directly to your phone. Please tap it and hit Save — that way you can call, text, or email me directly any time you need help with your plan."</MustRead>
          <RedNote>Confirm they saved the contact before moving on.</RedNote>
          <MustRead>"I'm your direct contact for anything Medicare-related going forward. Take down my number as well just in case."</MustRead>
          <Chk checked={!!sec.postSubmit} onClick={()=>done("postSubmit")}>Post-submission script read — member saved contact card ✓</Chk>
        </Card>

        {/* ── 14. HOSPITAL INDEMNITY PITCH ── */}
        <Card>
          <SecHead num={14} icon="🏥" title="Hospital Indemnity Pitch — ALL Standard & CSNP Plans" done={sec.indemnityPitch} />
          <RedNote>Pitch Hospital Indemnity on ALL Standard and CSNP plans — this is required.</RedNote>
          <MustRead>"Before we wrap up — this plan includes a hospital copay if you're ever admitted. I want to show you a low-cost option that offsets that. It's called a Hospital Indemnity plan. For a small monthly premium, you receive a cash benefit paid directly to you for each day you spend in the hospital — no restrictions on how you use it. Grab your pen and paper one more time and let's look at your options."</MustRead>
          <RedNote>Match their inpatient copay and MOOP where possible. If budget is a concern, find a lower benefit amount they're comfortable with.</RedNote>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <div style={{ flex:"1 1 45%", marginBottom:10 }}>
              <div style={{ fontSize:10, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>Daily Hospital Benefit to Match</div>
              <input value={plan.dailyHospitalBenefit||""} onChange={e=>up("dailyHospitalBenefit",e.target.value)} placeholder="$ per day" style={{ width:"100%", boxSizing:"border-box", background:"#fff", border:"1px solid #c00000", borderRadius:5, padding:"7px 10px", fontSize:13, color:"#c00000", fontWeight:"bold" }} />
            </div>
            <div style={{ flex:"1 1 45%", marginBottom:10 }}>
              <div style={{ fontSize:10, color:"#c00000", textTransform:"uppercase", marginBottom:3, fontWeight:"bold" }}>MOOP to Match</div>
              <input value={plan.moopToMatch||""} onChange={e=>up("moopToMatch",e.target.value)} placeholder="$" style={{ width:"100%", boxSizing:"border-box", background:"#fff", border:"1px solid #c00000", borderRadius:5, padding:"7px 10px", fontSize:13, color:"#c00000", fontWeight:"bold" }} />
            </div>
          </div>
          <Chk checked={!!sec.indemnityPitch} onClick={()=>done("indemnityPitch")}>Hospital Indemnity pitched ✓</Chk>
        </Card>

        {/* ── 15. AFTER INDEMNITY — WELCOME PACKET SCRIPT ── */}
        <Card>
          <SecHead num={15} icon="📬" title="After Indemnity Pitch — Welcome Packet Script" done={sec.welcomePacket} />
          <MustRead>"Expect your welcome packet within 10 to 14 days — it includes your plan cards and Summary of Benefits. The Evidence of Coverage is available on <strong>{plan.carrier||"[Carrier]"}</strong>'s website or by calling their customer service line. You'll find their contact information and hours in your welcome packet."</MustRead>
          <MustRead>"Your current carrier will call you — don't let that alarm you. They'll make it sound like your coverage is being cancelled immediately. It won't be. Your current coverage runs through the end of the month, and <strong>{plan.carrier||"[Carrier]"}</strong> picks up on <strong>{plan.startDate||"[effective date]"}</strong> without a gap."</MustRead>
          <MustRead>"If you have any questions about your plan, call me directly. Medicare has a lot of misleading ads and scams — I'm your resource going forward. I'll get back to you within 24 hours."</MustRead>
          <MustRead>"You have the right to cancel this enrollment at any time before the effective date by calling the plan directly."</MustRead>
          <MustRead>"The best thing you can do for someone you care about is share my number. If you know anyone who could use help reviewing their Medicare options, I'd love to take care of them the same way I took care of you today."</MustRead>
          <Chk checked={!!sec.welcomePacket} onClick={()=>done("welcomePacket")}>Welcome packet script read — cancellation warning given — referral asked ✓</Chk>
        </Card>

        {/* ── 16. TPV TRANSFER ── */}
        <Card>
          <SecHead num={16} icon="📞" title="TPV Transfer" done={sec.tpvTransfer} />
          <RedNote>Initiate TPV transfer now. Stay on the line until TPV has confirmed doctors and medications — do NOT disconnect early.</RedNote>
          <MustRead>"I'm going to add a third-party verifier to our call to finalize your enrollment. One moment please." <em>(Transfer to TPV queue)</em></MustRead>
          <MustRead>"Hi, I have <strong>{fullName||"[member name]"}</strong> on the line — phone number <strong>{bene.phone||"[member phone number]"}</strong>. Are you able to pull them up?"</MustRead>
          <MustRead>"Great — thank you both. You're all set, and I hope you enjoy your new benefits!" <em>(Remain on line until TPV confirms DRs and Meds, then exit)</em></MustRead>
          <Chk checked={!!sec.tpvTransfer} onClick={()=>done("tpvTransfer")}>TPV transfer complete — remained on line until DRs and Meds confirmed ✓</Chk>
        </Card>

        {/* ── 17. GENERATE ENROLLMENT LINK ── */}
        <div style={{ background:"#ffffff", border:`2px solid ${C.teal}`, borderRadius:12, padding:18, marginBottom:16, boxShadow:"0 4px 16px rgba(29,173,168,0.15)" }}>
          <SecHead num={17} icon="🔗" title="Generate Enrollment Link & Send" done={!!generated} />
          {!generated ? <>
            <div style={{ fontSize:13, color:"#1a2e4a", lineHeight:1.7, marginBottom:12 }}>
              All steps complete for <strong style={{color:C.teal}}>{fullName||"beneficiary"}</strong>. Generate their unique enrollment link — pre-filled with all their info so they just review, agree, and sign.
            </div>
            {Object.values(sec).filter(Boolean).length < 12 && (
              <Warn>{12 - Object.values(sec).filter(Boolean).length} steps still incomplete. Complete all steps for full compliance before sending.</Warn>
            )}
            <Btn onClick={generateLink} disabled={saving}>{saving?"Generating...":"🔗 Generate Enrollment Link"}</Btn>
          </> : <>
            <div style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:8, padding:12, marginBottom:12, textAlign:"center" }}>
              <div style={{ fontSize:22, marginBottom:4 }}>✅</div>
              <div style={{ fontSize:14, color:C.green, fontWeight:"bold" }}>Enrollment Link Created</div>
              <div style={{ fontSize:11, color:"#4a5a7a", marginTop:2 }}>Enrollment ID: {generated}</div>
            </div>

            {/* SMS Block */}
            <div style={{ background:"rgba(29,173,168,0.06)", border:"1px solid rgba(29,173,168,0.25)", borderRadius:8, padding:12, marginBottom:10 }}>
              <div style={{ fontSize:10, color:C.teal, letterSpacing:1.5, textTransform:"uppercase", marginBottom:6, fontWeight:"bold" }}>📱 Enrollment Text Message</div>
              <div style={{ fontSize:12, color:"#1a2e4a", lineHeight:1.7 }}>{smsText}</div>
              <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
                <button onClick={()=>copy(smsText,"sms")} style={{ background:"#ffffff", border:`1px solid ${C.teal}`, borderRadius:5, color:C.teal, fontSize:11, padding:"6px 14px", cursor:"pointer", fontWeight:"bold" }}>{copied==="sms"?"✓ Copied!":"Copy Text"}</button>
                <button onClick={()=>window.open(`sms:${bene.phone}?body=${encodeURIComponent(smsText)}`)} style={{ background:`linear-gradient(90deg,${C.teal},${C.teal2})`, border:"none", borderRadius:5, color:"#ffffff", fontSize:11, padding:"6px 14px", cursor:"pointer", fontWeight:"bold" }}>📲 Open in Messages</button>
              </div>
            </div>

            {/* Sunfire / TrueTrack Copy Block */}
            <div style={{ background:"rgba(29,173,168,0.06)", border:"1px solid rgba(29,173,168,0.25)", borderRadius:8, padding:12 }}>
              <div style={{ fontSize:10, color:C.teal, letterSpacing:1.5, textTransform:"uppercase", marginBottom:6, fontWeight:"bold" }}>☀️ Sunfire / TrueTrack — Copy & Paste</div>
              <div style={{ fontSize:11, color:"#1a2e4a", fontFamily:"monospace", lineHeight:1.8, whiteSpace:"pre-wrap" }}>{sunfireBlock}</div>
              <button onClick={()=>copy(sunfireBlock,"sunfire")} style={{ marginTop:8, background:"#ffffff", border:`1px solid ${C.teal}`, borderRadius:5, color:C.teal, fontSize:11, padding:"6px 14px", cursor:"pointer", fontWeight:"bold" }}>{copied==="sunfire"?"✓ Copied!":"Copy All for Sunfire"}</button>
            </div>
          </>}
        </div>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BENEFICIARY APP
// ═══════════════════════════════════════════════════════════════════════════════
function BeneficiaryApp({ enrollId, onBack, isDesktop=false }) {
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idInput, setIdInput] = useState("");
  const [agreements, setAgreements] = useState({});
  const [signature, setSignature] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(()=>{ if(enrollId) load(enrollId); else setLoading(false); },[enrollId]);
  const load=async(id)=>{ setLoading(true); const d=await store.get(`enroll:${id}`,true); setEnrollment(d); if(d?.status==="completed") setSubmitted(true); setLoading(false); };
  const allAgreed=BEN_AGREEMENTS.every(a=>agreements[a.id]) && agreements.mbiConfirmed && (enrollment?.bene?.hasMedicaid!=="Yes" || agreements.medicaidConfirmed);
  const submit=async()=>{
    if(!signature.trim()) return;
    await store.set(`enroll:${enrollment.id}`,{ ...enrollment, status:"completed", beneficiarySignature:signature, agreements, completedAt:new Date().toISOString() },true);
    setSubmitted(true);
  };

  const wrap={ fontFamily:"'Helvetica Neue',Arial,sans-serif", background:C.bg, minHeight:"100vh", color:C.text, paddingBottom:60 };
  const crd={ background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:14, boxShadow:"0 2px 8px rgba(58,125,156,0.08)" };
  const row=(k,v)=>(<div style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}><span style={{color:C.mid}}>{k}</span><span style={{color:C.navy,fontWeight:"bold",textAlign:"right",maxWidth:"58%"}}>{v||"—"}</span></div>);

  if(loading) return <div style={{...wrap,display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{color:C.mid}}>Loading...</div></div>;

  if(!enrollment && !enrollId) return (
    <div style={{...wrap,padding:24}}>
      <button onClick={onBack} style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",color:C.header,fontSize:14,cursor:"pointer",marginBottom:20,padding:"6px 14px",borderRadius:6,fontWeight:"bold"}}>‹ Back</button>
      <div style={{textAlign:"center",marginBottom:28}}><div style={{fontSize:36,marginBottom:8}}>📱</div><h2 style={{color:C.accent,margin:0}}>Your Enrollment</h2><p style={{color:C.mid,fontSize:13,marginTop:6}}>Enter your enrollment ID from the text your agent sent you.</p></div>
      <div style={crd}><F lbl="Enrollment ID" value={idInput} onChange={setIdInput} placeholder="e.g. A1B2C3D4E" /><Btn onClick={()=>idInput.trim()&&load(idInput.trim().toUpperCase())}>Look Up My Enrollment</Btn></div>
    </div>
  );

  if(!enrollment) return (
    <div style={{...wrap,padding:24}}>
      <button onClick={onBack} style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",color:C.header,fontSize:14,cursor:"pointer",marginBottom:20,padding:"6px 14px",borderRadius:6,fontWeight:"bold"}}>‹ Back</button>
      <div style={{...crd,textAlign:"center"}}><div style={{fontSize:28,marginBottom:8}}>❌</div><div style={{color:C.red}}>Enrollment not found. Check the ID and try again.</div></div>
    </div>
  );

  if(submitted) {
    const sobUrl = getSOBLink(enrollment.plan?.carrier);
    return (
      <div style={{...wrap, padding:0}}>
        {/* Header */}
        <div style={{background:"#ffffff", borderBottom:"3px solid #3ecfcf", padding:"20px 20px 16px", textAlign:"center"}}>
          <div style={{fontSize:22,fontWeight:"bold",color:"#0a1a2e",letterSpacing:0.5}}>Complete Care Marketing</div>
          <div style={{fontSize:11,color:"#3a7d9c",letterSpacing:0.5,marginTop:4}}>Medicare Enrollment Services</div>
          <div style={{fontSize:12,color:"#1a2e4a",fontWeight:"bold",marginTop:6}}>Your Medicare Enrollment Reference</div>
        </div>

        <div style={{padding:"20px 16px"}}>

          {/* Enrollment confirmed banner */}
          <div style={{background:C.greenBg,border:`1px solid ${C.greenBorder}`,borderRadius:12,padding:"14px 16px",marginBottom:14,textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:6}}>✅</div>
            <div style={{fontSize:16,color:C.green,fontWeight:"bold"}}>Enrollment Confirmed</div>
            <div style={{fontSize:12,color:C.mid,marginTop:4}}>Thank you, {enrollment.bene?.firstName}. Your enrollment has been submitted and confirmed.</div>
            <div style={{fontSize:11,color:C.dim,marginTop:6,fontFamily:"monospace"}}>Confirmation ID: {enrollment.id}</div>
          </div>

          {/* Plan details */}
          <div style={{background:"#ffffff",border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14,boxShadow:"0 2px 8px rgba(58,125,156,0.08)"}}>
            <div style={{fontSize:11,color:C.header,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your Plan</div>
            {[
              ["Plan Name", enrollment.plan?.planName],
              ["Carrier", enrollment.plan?.carrier],
              ["Plan Type", enrollment.plan?.planType],
              ["Effective Date", enrollment.plan?.startDate],
              ["SEP / Enrollment Period", enrollment.plan?.sepFound],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.mid}}>{k}</span>
                <span style={{color:C.navy,fontWeight:"bold",textAlign:"right",maxWidth:"58%"}}>{v||"—"}</span>
              </div>
            ))}
            {/* SOB Link */}
            <div style={{marginTop:12}}>
              <a href={sobUrl} target="_blank" rel="noopener noreferrer"
                style={{display:"block",width:"100%",boxSizing:"border-box",padding:"11px 16px",background:`linear-gradient(90deg,${C.header},${C.teal})`,borderRadius:8,color:"#ffffff",fontSize:13,fontWeight:"bold",textAlign:"center",textDecoration:"none"}}>
                📄 View Your Summary of Benefits
              </a>
              <div style={{fontSize:10,color:C.dim,textAlign:"center",marginTop:4}}>Opens {enrollment.plan?.carrier||"carrier"} plan documents</div>
            </div>
          </div>

          {/* Your PCP */}
          <div style={{background:"#ffffff",border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14,boxShadow:"0 2px 8px rgba(58,125,156,0.08)"}}>
            <div style={{fontSize:11,color:C.header,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your Primary Care Physician</div>
            {[
              ["PCP Name", enrollment.plan?.pcpName],
              ["PCP Phone", enrollment.plan?.pcpPhone],
              ["PCP ID", enrollment.plan?.pcpId],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.mid}}>{k}</span>
                <span style={{color:C.navy,fontWeight:"bold",textAlign:"right",maxWidth:"58%"}}>{v||"—"}</span>
              </div>
            ))}
          </div>

          {/* Agent contact card — permanent reference */}
          <div style={{background:C.header,borderRadius:12,padding:16,marginBottom:14,boxShadow:"0 4px 16px rgba(58,125,156,0.25)"}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your Agent</div>
            <div style={{fontSize:18,color:"#ffffff",fontWeight:"bold",marginBottom:4}}>{enrollment.agent?.name||"Your Agent"}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginBottom:12}}>{enrollment.agent?.company||"Complete Care Marketing, Inc."}</div>
            {enrollment.agent?.phone && (
              <a href={`tel:${enrollment.agent.phone}`}
                style={{display:"block",padding:"11px 16px",background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:8,color:"#ffffff",fontSize:14,fontWeight:"bold",textAlign:"center",textDecoration:"none",marginBottom:8}}>
                📞 Call Your Agent
              </a>
            )}
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:8}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:C.teal}}/>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Your agent is your dedicated Medicare resource — call anytime with questions</span>
            </div>
          </div>

          {/* Agent office contact */}
          <div style={{background:"#ffffff",border:`2px solid ${C.teal}`,borderRadius:12,padding:16,marginBottom:14,textAlign:"center"}}>
            <div style={{fontSize:11,color:C.mid,letterSpacing:1,textTransform:"uppercase",marginBottom:6}}>Complete Care Marketing — Support Line</div>
            <div style={{fontSize:20,fontWeight:"bold",color:C.navy}}>{enrollment.agent?.phone||"Contact Your Agent"}</div>
            <div style={{fontSize:12,color:C.mid,marginTop:4}}>{enrollment.agent?.email||""}</div>
            <div style={{fontSize:11,color:C.dim,marginTop:6,lineHeight:1.5}}>Medicare scams are common. Your agent is your trusted point of contact — call before making any changes to your coverage.</div>
          </div>

          {/* Right to cancel */}
          <div style={{background:"rgba(232,160,48,0.08)",border:`1px solid rgba(232,160,48,0.3)`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
            <div style={{fontSize:12,color:C.warn,fontWeight:"bold",marginBottom:4}}>⚠️ Your Right to Cancel</div>
            <div style={{fontSize:12,color:C.mid,lineHeight:1.6}}>You have the right to cancel your enrollment. If you choose to do so, please call the plan before the effective date of {enrollment.plan?.startDate||"your effective date"}.</div>
          </div>

          {/* Footer */}
          <div style={{textAlign:"center",padding:"8px 0 20px"}}>
            <div style={{fontSize:10,color:C.dim}}>Bookmark or screenshot this page to keep your plan information handy.</div>
            <div style={{fontSize:10,color:C.dim,marginTop:4}}>Enrollment ID: {enrollment.id} · {new Date(enrollment.completedAt||enrollment.createdAt).toLocaleDateString()}</div>
          </div>

        </div>
      </div>
    );
  }

  const {bene,plan,meds,agent}=enrollment;
  const fullName=`${bene?.firstName||""} ${bene?.lastName||""}`.trim();

  return (
    <div style={wrap}>
      <div style={{background:"#ffffff",borderBottom:`3px solid ${C.teal}`,padding:"12px 18px 10px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:13,fontWeight:"bold",color:C.header,marginBottom:4}}>Complete Care Marketing</div>
        <div style={{fontSize:16,color:C.header,fontWeight:"bold"}}>📋 Your Enrollment Review</div>
        <div style={{fontSize:12,color:C.mid,marginTop:2}}>{plan?.planName||"Your Plan"} — Prepared by {agent?.name||"Your Agent"}</div>
      </div>
      <div style={{display:"flex",background:"#ffffff",borderBottom:`1px solid ${C.border}`}}>
        {[{i:0,l:"1. Review"},{i:1,l:"2. Agreements"},{i:2,l:"3. Sign"}].map(t=>(
          <div key={t.i} onClick={()=>setStep(t.i)} style={{flex:1,textAlign:"center",padding:"10px 4px",fontSize:11,color:step===t.i?C.teal:C.mid,fontWeight:step===t.i?"bold":"normal",borderBottom:step===t.i?`2px solid ${C.teal}`:"2px solid transparent",cursor:"pointer"}}>{t.l}</div>
        ))}
      </div>
      <div style={{padding: isDesktop ? "28px 32px" : "18px 14px", maxWidth: isDesktop ? 900 : "100%", margin:"0 auto"}}>
        {step===0 && <>
          <div style={crd}>
            <div style={{fontSize:11,color:C.accent,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your Information</div>
            {row("Full Name",fullName)}
            {row("Date of Birth",bene?.dob)}
            {row("Phone",bene?.phone)}
            {row("Address",`${bene?.street||""}, ${bene?.city||""}, ${bene?.state||""} ${bene?.zip||""}`)}

            {/* MBI confirmation */}
            <div style={{marginTop:10,background:"rgba(58,125,156,0.06)",border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 13px",marginBottom:8}}>
              <div style={{fontSize:10,color:C.mid,textTransform:"uppercase",letterSpacing:1,marginBottom:4,fontWeight:"bold"}}>Medicare ID (MBI)</div>
              <div style={{fontSize:16,color:C.navy,fontWeight:"bold",fontFamily:"monospace",letterSpacing:1,marginBottom:8}}>{bene?.mbi||"—"}</div>
              <div onClick={()=>setAgreements(p=>({...p,mbiConfirmed:!p.mbiConfirmed}))} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                <div style={{width:22,height:22,borderRadius:5,border:agreements.mbiConfirmed?`2px solid ${C.teal}`:`2px solid ${C.border}`,background:agreements.mbiConfirmed?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12,color:"#fff",fontWeight:"bold"}}>{agreements.mbiConfirmed?"✓":""}</div>
                <span style={{fontSize:13,color:C.navy,fontWeight:"500"}}>I confirm this is my Medicare ID number</span>
              </div>
            </div>

            {/* Medicaid confirmation - only show if has Medicaid */}
            {bene?.hasMedicaid==="Yes" && (
              <div style={{background:"rgba(58,125,156,0.06)",border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 13px",marginBottom:8}}>
                <div style={{fontSize:10,color:C.mid,textTransform:"uppercase",letterSpacing:1,marginBottom:4,fontWeight:"bold"}}>Medicaid Number</div>
                <div style={{fontSize:16,color:C.navy,fontWeight:"bold",fontFamily:"monospace",letterSpacing:1,marginBottom:8}}>{bene?.medicaidNum||"—"}</div>
                <div style={{fontSize:11,color:C.mid,marginBottom:8}}>Medicaid Level: <strong style={{color:C.navy}}>{bene?.medicaidLevel||"—"}</strong></div>
                <div onClick={()=>setAgreements(p=>({...p,medicaidConfirmed:!p.medicaidConfirmed}))} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <div style={{width:22,height:22,borderRadius:5,border:agreements.medicaidConfirmed?`2px solid ${C.teal}`:`2px solid ${C.border}`,background:agreements.medicaidConfirmed?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12,color:"#fff",fontWeight:"bold"}}>{agreements.medicaidConfirmed?"✓":""}</div>
                  <span style={{fontSize:13,color:C.navy,fontWeight:"500"}}>I confirm this is my Medicaid number</span>
                </div>
              </div>
            )}
          </div>
          <div style={crd}>
            <div style={{fontSize:11,color:C.accent,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your New Plan</div>
            {row("Plan Name",plan?.planName)}
            {row("Carrier",plan?.carrier)}
            {row("Plan Type",plan?.planType)}
            {row("Effective Date",plan?.startDate)}
            {row("Replacing",plan?.currentCarrier)}
            {row("Your PCP",plan?.pcpName)}
            {row("PCP Phone",plan?.pcpPhone)}
            <div style={{marginTop:8,background:"rgba(74,154,234,0.08)",border:"1px solid rgba(74,154,234,0.3)",borderRadius:7,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:C.accent,letterSpacing:1,textTransform:"uppercase",marginBottom:4,fontWeight:"bold"}}>Your Enrollment Period (SEP)</div>
              <div style={{fontSize:13,color:C.text,lineHeight:1.5}}>{plan?.sepFound||"—"}</div>
              <div style={{fontSize:11,color:C.mid,marginTop:4}}>This is the reason you qualify to enroll at this time. You will be asked to confirm this below.</div>
            </div>
          </div>
          {meds?.length>0 && <div style={crd}><div style={{fontSize:11,color:C.accent,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:12}}>Your Medications</div>{meds.map((m,i)=><div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid rgba(26,58,110,0.4)`,fontSize:13}}><span style={{color:C.text}}>{m.name}</span><span style={{color:C.mid,fontSize:11}}>{m.dosage}{m.frequency?` | ${m.frequency}/day`:""}</span></div>)}</div>}
          <Btn onClick={()=>setStep(1)}>Review & Sign Agreements →</Btn>
        </>}
        {step===1 && <>
          <div style={crd}>
            <div style={{fontSize:11,color:C.accent,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:8}}>Please Check Each Box</div>
            <p style={{fontSize:12,color:C.mid,margin:"0 0 10px",lineHeight:1.5}}>Read each statement carefully and check the box to confirm your understanding.</p>
            {BEN_AGREEMENTS.map(item=><Chk key={item.id} checked={!!agreements[item.id]} onClick={()=>setAgreements(p=>({...p,[item.id]:!p[item.id]}))}>{item.label}</Chk>)}
          </div>
          <Btn onClick={()=>allAgreed&&setStep(2)} disabled={!allAgreed}>{allAgreed?"Proceed to Sign →":`Check all ${BEN_AGREEMENTS.length} boxes to continue`}</Btn>
        </>}
        {step===2 && <>
          <div style={crd}>
            <div style={{fontSize:11,color:C.accent,letterSpacing:2,textTransform:"uppercase",fontWeight:"bold",marginBottom:10}}>Identity Confirmation</div>
            <p style={{fontSize:13,color:C.text,lineHeight:1.6,margin:"0 0 14px"}}>Please confirm your identity to complete your enrollment.</p>

            {/* Who is completing this */}
            <div style={{fontSize:11,color:C.mid,textTransform:"uppercase",letterSpacing:1,marginBottom:6,fontWeight:"bold"}}>Who is completing this enrollment?</div>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <div onClick={()=>setSignature("self")} style={{flex:1,textAlign:"center",padding:"11px 8px",borderRadius:8,border:`2px solid ${signature==="self"?C.teal:C.border}`,background:signature==="self"?C.tealBg:"#fff",cursor:"pointer"}}>
                <div style={{fontSize:18,marginBottom:4}}>👤</div>
                <div style={{fontSize:13,fontWeight:"bold",color:signature==="self"?C.teal:C.navy}}>I am the Beneficiary</div>
                <div style={{fontSize:11,color:C.mid,marginTop:2}}>{fullName}</div>
              </div>
              <div onClick={()=>setSignature("poa")} style={{flex:1,textAlign:"center",padding:"11px 8px",borderRadius:8,border:`2px solid ${signature==="poa"?C.teal:C.border}`,background:signature==="poa"?C.tealBg:"#fff",cursor:"pointer"}}>
                <div style={{fontSize:18,marginBottom:4}}>📋</div>
                <div style={{fontSize:13,fontWeight:"bold",color:signature==="poa"?C.teal:C.navy}}>I have Power of Attorney</div>
                <div style={{fontSize:11,color:C.mid,marginTop:2}}>Acting on behalf of {fullName}</div>
              </div>
            </div>

            {/* POA disclosure */}
            {signature==="poa" && (
              <div style={{background:"rgba(192,0,0,0.06)",border:"1px solid #c00000",borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:12,color:"#c00000",fontWeight:"bold",marginBottom:6}}>⚠️ Power of Attorney Disclosure</div>
                <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
                  By selecting Power of Attorney, you confirm that you hold a valid Durable Power of Attorney that specifically grants you the authority to make health insurance enrollment decisions on behalf of <strong>{fullName}</strong>. Please be aware that Medicare and/or the plan carrier may contact you at any time to verify the Power of Attorney documentation. You may be required to provide a copy of the POA document. Providing false representation of POA authority is a federal offense.
                </div>
              </div>
            )}

            {signature && (
              <div style={{background:C.tealBg,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"11px 14px",marginBottom:14}}>
                <div style={{fontSize:12,color:C.teal,fontWeight:"bold",marginBottom:4}}>✓ Confirmed</div>
                <div style={{fontSize:12,color:C.mid,lineHeight:1.5}}>
                  {signature==="self"
                    ? `I, ${fullName}, confirm that I am the Medicare beneficiary and all information in this enrollment is accurate. I am making this enrollment decision of my own free will.`
                    : `I confirm I hold a valid Durable Power of Attorney authorizing me to make this Medicare Advantage enrollment decision on behalf of ${fullName}. I understand Medicare may verify this authorization at any time.`
                  }
                </div>
                <div style={{fontSize:10,color:C.dim,marginTop:6}}>Date: {new Date().toLocaleDateString()} · Enrollment ID: {enrollment.id}</div>
              </div>
            )}
          </div>
          <Btn onClick={submit} disabled={!signature}>✅ Submit My Enrollment</Btn>
        </>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANAGER APP — Approval Queue + Enrollments
// ═══════════════════════════════════════════════════════════════════════════════
function ManagerApp({ onBack, isDesktop=false }) {
  const [tab, setTab] = useState("queue");
  const [approvals, setApprovals] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [copied, setCopied] = useState("");
  const [managerNote, setManagerNote] = useState("");

  useEffect(()=>{ loadAll(); const interval=setInterval(loadAll,10000); return ()=>clearInterval(interval); },[]);

  const loadAll=async()=>{
    setLoading(true);
    // Load approvals
    const aKeys=await store.list("approval:",true);
    const allApprovals=await Promise.all(aKeys.map(async k=>{ const id=k.replace("approval:",""); return store.get(`approval:${id}`,true); }));
    // Sort: pending+clientOnLine first (red), then pending no client (yellow), then resolved
    const sorted = allApprovals.filter(Boolean).sort((a,b)=>{
      const score = x => x.status==="pending"&&x.clientOnLine ? 0 : x.status==="pending" ? 1 : x.status==="approved" ? 2 : 3;
      return score(a)-score(b) || new Date(b.timestamp)-new Date(a.timestamp);
    });
    setApprovals(sorted);
    // Load enrollments
    const eKeys=await store.list("enroll:",true);
    const allEnroll=await Promise.all(eKeys.map(async k=>{ const id=k.replace("enroll:",""); return store.get(id,true); }));
    setEnrollments(allEnroll.filter(Boolean).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)));
    setLoading(false);
  };

  const approveRequest = async (req, approved) => {
    const updated = { ...req, status: approved?"approved":"declined", managerNote, resolvedAt: new Date().toISOString() };
    await store.set(`approval:${req.id}`, updated, true);
    setManagerNote("");
    setSelectedApproval(null);
    loadAll();
  };

  const wrap={ fontFamily:"'Helvetica Neue',Arial,sans-serif", background:C.bg, minHeight:"100vh", color:C.text, paddingBottom:60 };
  const crd={ background:"#ffffff", border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:14, boxShadow:"0 2px 8px rgba(58,125,156,0.08)" };

  const pendingUrgent = approvals.filter(a=>a.status==="pending"&&a.clientOnLine).length;
  const pendingNormal = approvals.filter(a=>a.status==="pending"&&!a.clientOnLine).length;

  // Color config per status
  const statusColor = (a) => {
    if(a.status==="pending"&&a.clientOnLine) return { bg:"rgba(192,0,0,0.07)", border:"#c00000", dot:"#c00000", label:"🔴 CLIENT ON LINE", labelColor:"#c00000" };
    if(a.status==="pending"&&!a.clientOnLine) return { bg:"rgba(232,160,48,0.08)", border:C.warn, dot:C.warn, label:"🟡 Pending Approval", labelColor:C.warn };
    if(a.status==="approved") return { bg:C.greenBg, border:C.greenBorder, dot:C.green, label:"🟢 Approved", labelColor:C.green };
    return { bg:"rgba(0,0,0,0.03)", border:C.border, dot:C.dim, label:"⚫ Resolved", labelColor:C.dim };
  };

  if(selectedApproval) {
    const sc = statusColor(selectedApproval);
    return (
      <div style={wrap}>
        <div style={{background:"#ffffff",borderBottom:`3px solid ${C.teal}`,padding:"12px 24px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <button onClick={()=>setSelectedApproval(null)} style={{background:C.tealBg,border:`1px solid ${C.tealBorder}`,color:C.header,fontSize:14,cursor:"pointer",padding:"4px 12px",borderRadius:6,fontWeight:"bold"}}>‹ Back</button>
          <div><div style={{fontSize:15,color:C.header,fontWeight:"bold"}}>Approval Request</div><div style={{fontSize:11,color:C.mid}}>{selectedApproval.agentName} — {selectedApproval.clientName}</div></div>
        </div>
        <div style={{padding:"18px 14px"}}>
          {/* Status badge */}
          <div style={{background:sc.bg,border:`2px solid ${sc.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14,textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:"bold",color:sc.labelColor}}>{sc.label}</div>
            {selectedApproval.clientOnLine && <div style={{fontSize:12,color:"#c00000",marginTop:4,fontWeight:"bold"}}>⚡ Agent has client ON THE LINE — respond immediately</div>}
          </div>
          {/* Details */}
          <div style={crd}>
            <div style={{fontSize:11,color:C.mid,textTransform:"uppercase",letterSpacing:1,marginBottom:10,fontWeight:"bold"}}>Request Details</div>
            {[
              ["Agent",selectedApproval.agentName],
              ["Client",selectedApproval.clientName],
              ["Client Phone",selectedApproval.clientPhone],
              ["Issue",selectedApproval.reason],
              ["Plan",selectedApproval.planName],
              ["Carrier",selectedApproval.carrier],
              ["Time",new Date(selectedApproval.timestamp).toLocaleTimeString()],
              ["Enrollment ID",selectedApproval.enrollId],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.mid}}>{k}</span>
                <span style={{color:C.navy,fontWeight:"bold",textAlign:"right",maxWidth:"60%"}}>{v||"—"}</span>
              </div>
            ))}
          </div>
          {selectedApproval.status==="pending" && <>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:C.mid,textTransform:"uppercase",letterSpacing:1,marginBottom:4,fontWeight:"bold"}}>Manager Note (optional)</div>
              <textarea value={managerNote} onChange={e=>setManagerNote(e.target.value)} placeholder="Add a note for the agent..." style={{width:"100%",boxSizing:"border-box",background:"#fff",border:`1px solid ${C.border}`,borderRadius:7,color:C.text,padding:"9px 12px",fontSize:13,minHeight:70,resize:"vertical",outline:"none"}} />
            </div>
            <button onClick={()=>approveRequest(selectedApproval,true)} style={{width:"100%",padding:14,background:C.green,border:"none",borderRadius:9,color:"#fff",fontSize:15,fontWeight:"bold",cursor:"pointer",marginBottom:10}}>✅ Approve — Agent is Clear to Proceed</button>
            <button onClick={()=>approveRequest(selectedApproval,false)} style={{width:"100%",padding:14,background:"#c00000",border:"none",borderRadius:9,color:"#fff",fontSize:15,fontWeight:"bold",cursor:"pointer",marginBottom:10}}>❌ Decline</button>
          </>}
          {selectedApproval.status!=="pending" && (
            <div style={{...crd,background:selectedApproval.status==="approved"?C.greenBg:C.redBg,border:`1px solid ${selectedApproval.status==="approved"?C.greenBorder:C.redBorder}`}}>
              <div style={{fontSize:13,color:selectedApproval.status==="approved"?C.green:C.red,fontWeight:"bold"}}>{selectedApproval.status==="approved"?"✅ Approved":"❌ Declined"} — {new Date(selectedApproval.resolvedAt).toLocaleString()}</div>
              {selectedApproval.managerNote && <div style={{fontSize:12,color:C.mid,marginTop:4}}>Note: {selectedApproval.managerNote}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  if(selected){
    const e=selected;
    const {bene,plan,meds,agent}=e;
    const fn=`${bene?.firstName||""} ${bene?.lastName||""}`.trim();

    return (
      <div style={wrap}>
        <div style={{background:"#ffffff",borderBottom:`3px solid ${C.teal}`,padding:"12px 24px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <button onClick={()=>setSelected(null)} style={{background:"rgba(58,125,156,0.1)",border:`1px solid ${C.border}`,color:C.header,fontSize:14,cursor:"pointer",padding:"5px 12px",borderRadius:6,fontWeight:"bold"}}>‹ Back</button>
          <div><div style={{fontSize:15,color:C.green,fontWeight:"bold"}}>{fn}</div><div style={{fontSize:11,color:C.mid}}>ID: {e.id} — {e.status==="completed"?"✅ Signed":"⏳ Pending"}</div></div>
        </div>
        <div style={{padding:"18px 14px"}}>
          <div style={{background:C.tealBg,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:C.teal,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>⚕</div>
            <div style={{fontSize:12,color:"#1a2e4a",fontWeight:"600",lineHeight:1.6}}>Open Sunfire or the carrier portal and use the <strong style={{color:C.teal}}>CCM Chrome extension</strong> in the toolbar to autofill all fields for this enrollment.</div>
          </div>
          {e.status==="completed"
            ? <div style={{...crd,background:C.greenBg,border:`1px solid ${C.greenBorder}`}}>
                <div style={{fontSize:11,color:C.green,letterSpacing:1.5,textTransform:"uppercase",fontWeight:"bold",marginBottom:8}}>✅ Beneficiary Signed</div>
                <div style={{fontSize:13,color:C.text}}>Signature: <strong>{e.beneficiarySignature}</strong></div>
                <div style={{fontSize:11,color:C.mid,marginTop:4}}>Completed: {new Date(e.completedAt).toLocaleString()}</div>
                <div style={{fontSize:11,color:C.mid,marginTop:4}}>Agreements confirmed: {BEN_AGREEMENTS.filter(a=>e.agreements?.[a.id]).length}/{BEN_AGREEMENTS.length}</div>
              </div>
            : <div style={{...crd,background:C.redBg,border:`1px solid ${C.redBorder}`}}><div style={{fontSize:13,color:C.red}}>⏳ Awaiting beneficiary signature</div></div>
          }
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {/* Header */}
      <div style={{background:"#ffffff",borderBottom:`3px solid ${C.teal}`,padding:"12px 24px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <button onClick={onBack} style={{background:C.tealBg,border:`1px solid ${C.tealBorder}`,color:C.header,fontSize:20,cursor:"pointer",padding:"4px 12px",borderRadius:6,fontWeight:"bold"}}>‹ Back</button>
        <div style={{flex:1}}>
          <div style={{fontSize:15,color:C.header,fontWeight:"bold"}}>📊 Manager Portal</div>
          {pendingUrgent>0 && <div style={{fontSize:11,color:C.red,fontWeight:"bold"}}>🔴 {pendingUrgent} agent{pendingUrgent>1?"s":""} with client ON THE LINE</div>}
        </div>
        <button onClick={loadAll} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,color:C.mid,fontSize:11,padding:"4px 12px",cursor:"pointer"}}>↻</button>
      </div>

      {/* Color Key */}
      <div style={{background:"#ffffff",borderBottom:`1px solid ${C.border}`,padding:"10px 16px",display:"flex",gap:12,flexWrap:"wrap"}}>
        <div style={{fontSize:11,color:C.mid,fontWeight:"bold",marginRight:4}}>KEY:</div>
        {[
          {dot:"#c00000",label:"Client ON LINE — urgent"},
          {dot:C.warn,label:"Pending — no client"},
          {dot:C.green,label:"Approved"},
          {dot:C.dim,label:"Resolved"},
        ].map(k=>(
          <div key={k.label} style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:k.dot,flexShrink:0}}/>
            <span style={{fontSize:10,color:C.mid}}>{k.label}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",background:"#ffffff",borderBottom:`1px solid ${C.border}`}}>
        <div onClick={()=>setTab("queue")} style={{flex:1,textAlign:"center",padding:"10px 4px",fontSize:12,color:tab==="queue"?C.teal:C.mid,fontWeight:tab==="queue"?"bold":"normal",borderBottom:tab==="queue"?`2px solid ${C.teal}`:"2px solid transparent",cursor:"pointer",position:"relative"}}>
          🔔 Approval Queue
          {(pendingUrgent+pendingNormal)>0 && <span style={{marginLeft:6,background:"#c00000",color:"#fff",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:"bold"}}>{pendingUrgent+pendingNormal}</span>}
        </div>
        <div onClick={()=>setTab("enrollments")} style={{flex:1,textAlign:"center",padding:"10px 4px",fontSize:12,color:tab==="enrollments"?C.teal:C.mid,fontWeight:tab==="enrollments"?"bold":"normal",borderBottom:tab==="enrollments"?`2px solid ${C.teal}`:"2px solid transparent",cursor:"pointer"}}>
          📋 Enrollments
        </div>
      </div>

      <div style={{padding: isDesktop ? "28px 32px" : "16px 14px", maxWidth: isDesktop ? 1100 : "100%", margin:"0 auto"}}>
        {loading && <div style={{textAlign:"center",color:C.mid,padding:40}}>Loading...</div>}

        {/* APPROVAL QUEUE TAB */}
        {!loading && tab==="queue" && <>
          {approvals.length===0 && <div style={{...crd,textAlign:"center"}}><div style={{fontSize:26,marginBottom:8}}>✅</div><div style={{color:C.mid}}>No pending approvals.</div></div>}
          {approvals.map(a=>{
            const sc=statusColor(a);
            return (
              <div key={a.id} onClick={()=>setSelectedApproval(a)} style={{background:sc.bg,border:`2px solid ${sc.border}`,borderRadius:12,padding:"14px 16px",marginBottom:10,cursor:"pointer",boxShadow:a.status==="pending"&&a.clientOnLine?"0 4px 16px rgba(192,0,0,0.2)":"0 2px 8px rgba(0,0,0,0.06)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <div style={{width:12,height:12,borderRadius:"50%",background:sc.dot,flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:"bold",color:sc.labelColor}}>{sc.label}</span>
                    </div>
                    <div style={{fontSize:14,fontWeight:"bold",color:C.navy}}>{a.clientName}</div>
                    <div style={{fontSize:12,color:C.mid,marginTop:2}}>Agent: {a.agentName}</div>
                    <div style={{fontSize:12,color:"#c00000",fontWeight:"bold",marginTop:2}}>{a.reason}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:2}}>{new Date(a.timestamp).toLocaleTimeString()} · {a.planName||"No plan"}</div>
                  </div>
                  <div style={{fontSize:22,color:sc.dot}}>›</div>
                </div>
                {a.status==="pending" && <div style={{marginTop:10,display:"flex",gap:8}}>
                  <button onClick={e=>{e.stopPropagation();store.set(`approval:${a.id}`,{...a,status:"approved",managerNote:"Quick approved",resolvedAt:new Date().toISOString()},true).then(loadAll);}} style={{flex:1,padding:"8px",background:C.green,border:"none",borderRadius:7,color:"#fff",fontSize:12,fontWeight:"bold",cursor:"pointer"}}>✅ Approve</button>
                  <button onClick={e=>{e.stopPropagation();setSelectedApproval(a);}} style={{flex:1,padding:"8px",background:"#fff",border:`1px solid ${C.border}`,borderRadius:7,color:C.mid,fontSize:12,cursor:"pointer"}}>View Details</button>
                </div>}
              </div>
            );
          })}
        </>}

        {/* ENROLLMENTS TAB */}
        {!loading && tab==="enrollments" && <>
          {enrollments.length===0 && <div style={{...crd,textAlign:"center"}}><div style={{fontSize:26,marginBottom:8}}>📭</div><div style={{color:C.mid}}>No enrollments yet.</div></div>}
          {enrollments.map(e=>{
            const name=`${e.bene?.firstName||""} ${e.bene?.lastName||""}`.trim();
            const done=e.status==="completed";
            return (
              <div key={e.id} onClick={()=>setSelected(e)} style={{...crd,cursor:"pointer",borderColor:done?C.greenBorder:C.border}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:"bold",color:C.text}}>{name||"Unknown"}</div>
                    <div style={{fontSize:11,color:C.mid,marginTop:2}}>{e.plan?.planName||"No plan"} — {e.plan?.carrier||""}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:2}}>ID: {e.id} · {new Date(e.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                    <span style={{fontSize:11,fontWeight:"bold",color:done?C.green:C.gold,background:done?C.greenBg:C.goldBg,border:`1px solid ${done?C.greenBorder:C.goldBorder}`,borderRadius:20,padding:"3px 10px"}}>{done?"✅ Signed":"⏳ Pending"}</span>
                    <span style={{fontSize:10,color:C.dim}}>Agent: {e.agent?.name||"—"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </>}
      </div>
    </div>
  );
}
